(() => {
    const CHARACTER_ROLES = [
      ['agile', 'Agile'],
      ['stealthy', 'Stealthy'],
      ['strong', 'Strong'],
      ['resilient', 'Resilient'],
      ['observant', 'Observant'],
      ['social', 'Social'],
      ['arcane', 'Arcane'],
      ['divine', 'Divine'],
      ['explorer', 'Explorer'],
      ['support', 'Support'],
      ['controller', 'Controller'],
      ['ranged', 'Ranged fighter']
    ];

    const CHARACTER_WEAKNESSES = [
      ['', 'None selected'],
      ['low-strength', 'Low Strength or poor lifting power'],
      ['low-dexterity', 'Low Dexterity or clumsy movement'],
      ['low-constitution', 'Low Constitution or poor endurance'],
      ['poor-perception', 'Poor Perception or easily distracted'],
      ['poor-navigation', 'Poor navigation or weak Survival skills'],
      ['poor-social', 'Poor social skills or visibly suspicious'],
      ['heavy-armor', 'Heavy armor or cumbersome equipment'],
      ['slow-speed', 'Low movement speed'],
      ['injured', 'Injured or physically impaired'],
      ['poor-swimmer', 'Poor swimmer'],
      ['fear-heights', 'Fear of heights'],
      ['fear-dark', 'Fear of darkness or no darkvision'],
      ['loud-gear', 'Loud or rattling equipment'],
      ['large-frame', 'Large, conspicuous, or hard to conceal'],
      ['small-frame', 'Small frame or limited reach'],
      ['limited-breath', 'Limited breath or respiratory weakness']
    ];

    const WEAKNESS_MATRIX = {
      'low-strength': {
        keywords: ['strength', 'athletics', 'force', 'lift', 'hold', 'climb', 'brace'],
        traits: ['difficult-terrain', 'collapsing', 'moving-machinery'],
        dc: 2,
        read: '{character} can already feel that this route will demand more raw force than comes naturally.',
        dm: 'Strength-based approaches are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'low-dexterity': {
        keywords: ['dexterity', 'acrobatics', 'stealth', 'balance', 'reflex', 'vault'],
        traits: ['slippery', 'vertical', 'cramped', 'traffic'],
        dc: 2,
        read: '{character} has less room for a clean step or quick correction than the route seems willing to allow.',
        dm: 'Dexterity-based approaches are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'low-constitution': {
        keywords: ['constitution', 'endure', 'exhaustion', 'breath', 'poison', 'cold', 'heat'],
        traits: ['toxic', 'freezing', 'burning', 'submerged'],
        dc: 2,
        read: 'The pace is already stripping breath and strength from {character}, turning endurance into a visible concern.',
        dm: 'Endurance and exposure checks are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'poor-perception': {
        keywords: ['perception', 'insight', 'notice', 'spot', 'read movement'],
        traits: ['dark', 'guarded', 'secret-passages'],
        dc: 2,
        read: '{character} has to commit before the route fully reveals what is waiting ahead.',
        dm: 'Perception and awareness approaches are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'poor-navigation': {
        keywords: ['survival', 'investigation', 'route', 'trail', 'direction', 'navigate'],
        traits: ['secret-passages', 'overgrown', 'magically-warped'],
        dc: 2,
        read: 'The route refuses to arrange itself into a pattern {character} can trust.',
        dm: 'Navigation and route-reading approaches are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'poor-social': {
        keywords: ['charisma', 'persuasion', 'deception', 'intimidation', 'crowd', 'guard'],
        traits: ['crowded', 'guarded', 'civilian-risk'],
        dc: 2,
        read: 'Every face in the route seems ready to misunderstand {character} at exactly the wrong moment.',
        dm: 'Social approaches are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'heavy-armor': {
        keywords: ['acrobatics', 'stealth', 'swim', 'balance', 'squeeze', 'climb'],
        traits: ['vertical', 'cramped', 'flooded', 'submerged', 'slippery'],
        dc: 2,
        read: 'Every plate, strap, and carried weight announces itself as {character} reaches the hardest part of the route.',
        dm: 'Heavy armor makes agile, quiet, climbing, and water-based approaches harder. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'slow-speed': {
        keywords: ['speed', 'sprint', 'direct line', 'keep moving', 'distance'],
        traits: ['exposed', 'traffic', 'difficult-terrain'],
        dc: 2,
        read: '{character} cannot spend movement as freely as the others, so every detour carries a sharper cost.',
        dm: 'Pure speed and distance-closing approaches are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      injured: {
        keywords: ['athletics', 'acrobatics', 'constitution', 'climb', 'jump', 'sprint'],
        traits: ['vertical', 'difficult-terrain', 'collapsing', 'slippery'],
        dc: 2,
        read: "Pain changes {character}'s movement before the obstacle even begins, forcing every action to be chosen carefully.",
        dm: 'Most strenuous physical approaches are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'poor-swimmer': {
        keywords: ['swim', 'water', 'breath'],
        traits: ['flooded', 'submerged'],
        dc: 3,
        read: 'The water turns the route into a threat aimed directly at {character}.',
        dm: 'Swimming and submerged movement are substantially harder for this character. The displayed DC already includes a +3 weakness modifier when relevant.'
      },
      'fear-heights': {
        keywords: ['height', 'drop', 'ledge', 'roof', 'climb', 'jump'],
        traits: ['vertical', 'exposed', 'strong-winds'],
        dc: 2,
        read: 'The open drop reaches {character} before the next handhold does.',
        dm: 'Exposed vertical movement is harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'fear-dark': {
        keywords: ['dark', 'light', 'sight', 'perception'],
        traits: ['dark'],
        dc: 2,
        read: 'The darkness closes around {character} as a physical pressure, not merely an absence of light.',
        dm: 'Actions that depend on moving or perceiving through darkness are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'loud-gear': {
        keywords: ['stealth', 'quiet', 'silence', 'misdirect'],
        traits: ['noisy', 'guarded', 'secret-passages'],
        dc: 2,
        read: "{character}'s equipment turns each hurried movement into a signal the opposition can follow.",
        dm: 'Stealth and misdirection are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'large-frame': {
        keywords: ['stealth', 'squeeze', 'narrow', 'hide', 'crowd'],
        traits: ['cramped', 'secret-passages', 'crowded'],
        dc: 2,
        read: "The route was not built for someone of {character}'s size to pass unnoticed or without slowing.",
        dm: 'Concealment and cramped movement are harder for this character. The displayed DC already includes a +2 weakness modifier when relevant.'
      },
      'small-frame': {
        keywords: ['strength', 'lift', 'hold', 'reach', 'force'],
        traits: ['moving-machinery', 'collapsing'],
        dc: 1,
        read: "The next mechanism sits just beyond {character}'s easy reach, turning leverage into part of the problem.",
        dm: 'Reach and leverage-based approaches are slightly harder for this character. The displayed DC already includes a +1 weakness modifier when relevant.'
      },
      'limited-breath': {
        keywords: ['constitution', 'breath', 'smoke', 'poison', 'swim', 'endure'],
        traits: ['burning', 'toxic', 'submerged'],
        dc: 3,
        read: 'The air itself becomes a limit for {character}, shortening the time available for every decision.',
        dm: 'Breath, smoke, poison, and submerged endurance are substantially harder. The displayed DC already includes a +3 weakness modifier when relevant.'
      }
    };

    const CHASE_MODE_LABELS = {
      foot: 'on foot',
      mounted: 'mounted',
      vehicle: 'in a wheeled vehicle or carriage',
      naval: 'aboard a ship or watercraft',
      flying: 'in flight',
      underwater: 'underwater',
      'magical-transit': 'through magical transit'
    };

    const ENVIRONMENT_LABELS = {
      village: 'village',
      city: 'city',
      'urban-district': 'urban district',
      dungeon: 'dungeon',
      castle: 'castle or fortress',
      labyrinth: 'labyrinth',
      caverns: 'cavern network',
      forest: 'forest',
      jungle: 'jungle or rainforest',
      road: 'open road or trade route',
      plains: 'plains or grassland',
      desert: 'desert or badlands',
      tundra: 'tundra or ice fields',
      mountains: 'mountain route',
      swamp: 'swamp',
      river: 'river or coastline',
      'open-sea': 'open sea',
      sky: 'open sky or aerial city',
      underwater: 'underwater realm',
      battlefield: 'battlefield or war zone',
      sewers: 'sewer network',
      ruins: 'ruins',
      planar: 'otherworldly place',
      custom: 'custom environment'
    };

    const CHASE_MODE_MATRIX = {
      foot: {
        label: 'On foot',
        movement: [
          'Footing, breath, and the ability to change direction without warning decide who controls the next few seconds.',
          'Every useful object in reach can become cover, a step, a barrier, or a reason to leave the obvious line.',
          'The chase remains intimate enough that a door, crowd, ledge, or fallen object can change the whole balance.'
        ],
        opportunities: [
          'A narrow line lets a single runner slip through before the route closes.',
          'A climbable surface creates a higher path that speed alone cannot claim.',
          'A movable object can block pursuit, create cover, or reveal a concealed way through.'
        ],
        risks: [
          'A missed step can separate the group or surrender the cleanest line.',
          'The fastest character can gain distance at the cost of leaving someone exposed.',
          'The route demands a sudden transition between sprinting, climbing, hiding, and forcing a way through.'
        ],
        dm: 'On-foot chases should reward interaction with architecture, crowds, terrain, doors, elevation, and cover rather than repeated running checks.',
        check: ['A relevant movement or route skill', 'Use the environment, teamwork, or a class feature to gain position instead of relying on speed alone.'],
        cost: 'The party keeps moving, but one runner loses equipment, footing, formation, or the protection of the group.',
        setback: 'The opposition reaches the same piece of terrain and can now interfere directly.'
      },
      mounted: {
        label: 'Mounted',
        movement: [
          'The chase is now a contest of trust, balance, and reading a living mount before it commits its weight.',
          'Hooves strike hard enough to turn loose ground, noise, and narrow gaps into immediate tactical problems.',
          'Riders must manage speed without exhausting, frightening, or injuring the creatures carrying them.'
        ],
        opportunities: [
          'A low barrier can be jumped, broken, or used to force the other riders into a slower line.',
          'A herd, remount, hitching rail, or pack animal offers a way to exchange speed for confusion.',
          'A steep slope or narrow path favors the rider who trusts the mount instead of fighting it.'
        ],
        risks: [
          'A frightened mount begins drifting away from the chosen line.',
          'A saddle strap, rein, or piece of tack starts to fail under the pace.',
          'The group risks losing formation when one mount clears an obstacle differently from the others.'
        ],
        dm: 'Mounted chases use Wisdom (Animal Handling), mount endurance, jumps, formation, tack, remounting, and the welfare of the animals. A good rider can avoid a physical weakness by letting the mount do the work.',
        check: ['Wisdom (Animal Handling)', 'Guide the mount through danger, calm it, preserve its endurance, or use its instincts to choose the line.'],
        cost: 'The mount clears the danger but becomes winded, frightened, injured, or separated from the rest of the group.',
        setback: 'A rider is thrown, forced to dismount, or carried onto a worse line while the opposition closes.'
      },
      vehicle: {
        label: 'Wheeled vehicle or carriage',
        movement: [
          'Momentum belongs to the vehicle now: every turn, axle, wheel, passenger, and piece of cargo affects what is possible.',
          'The driver can gain speed only by choosing what the vehicle is willing to hit, scrape, or leave behind.',
          'The chase stretches across the whole vehicle, from the reins and wheels to passengers fighting for balance.'
        ],
        opportunities: [
          'A banked turn, ramp, alley, or roadside slope can become a shortcut if the driver commits before seeing the whole landing.',
          'Cargo can be shifted, cut loose, thrown, or used to stabilize the vehicle.',
          'A second vehicle, team of draft animals, or roadside mechanism can be commandeered or collided with deliberately.'
        ],
        risks: [
          'One wheel begins to wobble after the last impact.',
          'Passengers and cargo slide toward the outside of a hard turn.',
          'The draft team panics as the route narrows around noise, flame, or another vehicle.'
        ],
        dm: 'Vehicle chases should use land vehicle proficiency, Animal Handling when draft animals matter, repairs, passenger actions, collisions, cargo, tight turns, and changing road surfaces.',
        check: ['Land vehicles or Dexterity', 'Control speed and angle through a turn, collision, jump, or narrowing route.'],
        cost: 'The vehicle keeps moving, but loses cargo, structural integrity, a wheel fitting, a passenger position, or the trust of the draft team.',
        setback: 'The vehicle spins, overturns partially, breaks an essential component, or is forced into a slower lane.'
      },
      naval: {
        label: 'Ship, boat, or watercraft',
        movement: [
          'Wind, current, crew, rigging, and the vessel itself now share control of the chase.',
          'The distance between vessels changes in long surges rather than individual steps, making every turn and sail adjustment consequential.',
          'No one acts alone aboard the craft: helm, sails, oars, ballast, weapons, and repairs all compete for attention.'
        ],
        opportunities: [
          'A narrow channel, wake, sandbar, bridge opening, or line of anchored boats offers a dangerous route the larger craft may not match.',
          'A loose sail, spare line, anchor, cargo boom, or boarding hook can change relative position without increasing speed.',
          'A change in wind or current can be anticipated, exploited, or deliberately created through magic.'
        ],
        risks: [
          'A line snaps and leaves sail or rigging uncontrolled.',
          'Water begins entering through a damaged seam or low rail.',
          'The crew loses rhythm just as the vessel needs to turn, accelerate, or avoid impact.'
        ],
        dm: 'Naval chases use water vehicle proficiency, crew coordination, wind, current, sails, oars, reefs, boarding, repairs, ballast, and vessel size. Give several characters simultaneous jobs.',
        check: ['Water vehicles', 'Helm, trim sail, coordinate oars, read current, or direct the crew through a dangerous maneuver.'],
        cost: 'The vessel gains position but loses sail, crew efficiency, cargo, hull integrity, or access to the safest water.',
        setback: 'The craft grounds, broaches, fouls its rigging, takes water, or allows the other vessel into boarding range.'
      },
      flying: {
        label: 'Flying',
        movement: [
          'The chase opens in three dimensions, where altitude, turning radius, lift, and the ground far below all matter.',
          'Speed is no longer enough; every flyer must decide when to climb, dive, brake, or use cover that does not exist at ground level.',
          'Wind and empty space make separation more dangerous because help may be only a few yards away and still impossible to reach.'
        ],
        opportunities: [
          'A tower, cloud bank, canyon wall, airship, flock, or suspended structure can break sight or provide a slingshot turn.',
          'A dive converts altitude into speed, while a climb can force heavier pursuers to lose momentum.',
          'A thermal, downdraft, magical current, or moving aerial platform can carry anyone who reads it correctly.'
        ],
        risks: [
          'A sudden crosswind pushes one flyer out of formation.',
          'A wing, spell, mount, or flying device begins losing lift.',
          'The chase drops beneath a safe recovery altitude while the next obstacle closes.'
        ],
        dm: 'Flying chases use three-dimensional positioning, altitude, wind, turning space, falling risk, aerial cover, flight sources, and rescue. A setback should create a recoverable fall or forced landing before arbitrary death.',
        check: ['Dexterity, Athletics, Acrobatics, or flight control', 'Climb, dive, bank, brake, draft, or hold formation through the aerial hazard.'],
        cost: 'The flyer keeps pace but loses altitude, formation, concentration, a carried item, or a safe route to the ground.',
        setback: 'A flyer stalls, collides, falls, or is forced into a landing zone chosen by the opposition.'
      },
      underwater: {
        label: 'Underwater',
        movement: [
          'Every direction is available, but current, buoyancy, pressure, breath, and poor communication make simple coordination difficult.',
          'The chase moves through water as much as through terrain; sound travels strangely and visible distance can vanish in a cloud of silt.',
          'Equipment, armor, light, and spoken magic behave differently enough that each choice must account for the medium itself.'
        ],
        opportunities: [
          'A current, bubble vent, kelp forest, wreck, reef tunnel, or rising column of warm water can provide speed or concealment.',
          'Buoyant cargo, weighted lines, air pockets, and aquatic creatures can become tools rather than scenery.',
          'A vertical route through depth lets the party trade pressure and visibility for a shorter line.'
        ],
        risks: [
          'A silt cloud erases both the route and the nearest ally.',
          'A current starts pulling the slowest swimmer toward a separate channel.',
          'Breath, pressure, or waterlogged equipment turns delay into a direct threat.'
        ],
        dm: 'Underwater chases use swimming, Athletics, breath, currents, buoyancy, pressure, visibility, communication, aquatic mounts, and three-dimensional movement. Apply poor-swimmer and limited-breath weaknesses when relevant.',
        check: ['Strength (Athletics) or an aquatic movement feature', 'Swim against current, control depth, preserve breath, or use the water itself to gain position.'],
        cost: 'The party advances but loses air, equipment, light, formation, or control of depth.',
        setback: 'A swimmer is swept away, trapped below, separated by silt, or forced to surface somewhere dangerous.'
      },
      'magical-transit': {
        label: 'Magical transit',
        movement: [
          'The chase advances by portals, shifting paths, living spells, teleportation circles, or other rules that do not respect ordinary distance.',
          'Position depends on understanding the transit system before the opposition exploits its next change.',
          'The route may demand choices about destination, identity, memory, attunement, or magical cost rather than raw speed.'
        ],
        opportunities: [
          'A half-open portal, unstable rune, ley-line junction, summoned conveyance, or repeating spatial loop can be redirected.',
          'A magical signature left by the opposition can be tracked, copied, disguised, or sent toward a false destination.',
          'The transit effect has a visible rule that can be obeyed, broken, or weaponized.'
        ],
        risks: [
          'The route begins sending travelers to slightly different destinations.',
          'A transit rune overloads and starts consuming charge, memory, time, or material components.',
          'The opposition changes the magical rule governing the next crossing.'
        ],
        dm: 'Magical-transit chases use Arcana, spellcasting, attunement, portal logic, teleportation errors, dimensional anchors, magical costs, and creative rule interactions. State visible rules before demanding a choice.',
        check: ['Intelligence (Arcana) or spellcasting ability', 'Read, stabilize, redirect, counterfeit, or interrupt the magical route.'],
        cost: 'The transit works but leaves a magical mark, consumes a resource, alters a destination, or separates arrival times.',
        setback: 'The party emerges divided, delayed, marked, or at a destination controlled by the opposition.'
      }
    };

    const OBJECTIVE_LABELS = {
      escape: 'escape the area',
      'lose-pursuers': 'lose the pursuers',
      'reach-place': 'reach a specific place',
      'catch-target': 'catch the target',
      'recover-object': 'recover or steal an object',
      'protect-person': 'protect or extract someone',
      'survive-timer': 'complete the objective before time expires',
      'stop-target': 'stop the target before it acts',
      custom: 'complete a custom objective'
    };

    const DIFFICULTY_DC = {
      forgiving: 11,
      standard: 13,
      challenging: 15,
      brutal: 17
    };

    const TONE_PROFILES = {
      cinematic: {
        opening: ['The world contracts to speed, impact, and the next impossible decision.', 'For one sharp instant, every route looks possible and none of them looks safe.'],
        pressure: ['The chase hits the route like a breaking wave.', 'Momentum becomes the only thing keeping disaster one step behind.'],
        final: ['The last stretch opens ahead, close enough to see and far enough to lose everything.', 'The chase reaches the point where one decision will define the whole escape.']
      },
      tense: {
        opening: ['Every footfall carries clearly, and every lost second matters.', 'The distance between both sides is measured in breaths now, not streets or corridors.'],
        pressure: ['No one has enough time to make the safe choice.', 'The smallest hesitation is immediately answered from behind.'],
        final: ['The objective is close, but so is the moment when the chase becomes a confrontation.', 'There is no spare distance left.']
      },
      dark: {
        opening: ['The route ahead looks less like an escape than another kind of trap.', 'The way forward is open, but nothing about it feels merciful.'],
        pressure: ['The place seems eager to decide who will be left behind.', 'Every answer the route offers carries a quieter threat inside it.'],
        final: ['The end of the chase waits ahead like a verdict.', 'The last path is visible, and it asks for a price.']
      },
      horror: {
        opening: ['Something in the dark keeps pace without ever sounding hurried.', 'The pursuit is close enough to feel, even when it cannot be seen.'],
        pressure: ['The sounds behind you stop at exactly the wrong moment.', 'For a heartbeat, the route goes silent, and the silence is worse.'],
        final: ['The last stretch feels less like safety than the mouth of something waiting.', 'The end is near, but whatever follows has not begun to tire.']
      },
      comic: {
        opening: ['The plan survives for almost three seconds before becoming a public disaster.', 'Everything would be going perfectly if the route had agreed to cooperate.'],
        pressure: ['The chase acquires one more unnecessary complication with almost professional timing.', 'Somewhere, someone has chosen the worst possible moment to be helpful.'],
        final: ['The end is in sight, along with several new reasons this should never have worked.', 'Victory is close enough to touch and unstable enough to fall over.']
      },
      heroic: {
        opening: ['For one breath, you have the chance to turn panic into legend.', 'The route ahead is dangerous, but it is also the kind of danger heroes are remembered for crossing.'],
        pressure: ['This is the moment when courage has to become action.', 'Someone must make the route possible for everyone else.'],
        final: ['The objective waits beyond one final act of nerve.', 'The last stretch offers no easy way through, only a worthy one.']
      },
      mysterious: {
        opening: ['The route seems to rearrange itself whenever no one is looking.', 'The pursuit is real, but the distance between you refuses to behave honestly.'],
        pressure: ['A familiar landmark appears where it cannot possibly be.', 'The next opening feels less discovered than offered.'],
        final: ['The objective is close, though the route no longer agrees on where close should be.', 'The last path reveals itself as though it has been waiting for this exact moment.']
      }
    };

    const SCALE_NARRATION = {
      compact: 'There is almost no room to build distance; every obstacle immediately becomes part of the confrontation.',
      contained: 'The route folds back on itself, keeping both sides close enough to hear and anticipate each other.',
      extended: 'The chase stretches across several connected spaces, and every turn can change who controls the route.',
      enormous: 'The route is vast enough for distance, shortcuts, and parallel paths to matter as much as speed.',
      labyrinthine: 'Direction matters more than distance here, and a wrong turn can put the opposition ahead instead of behind.'
    };

    const VISIBILITY_NARRATION = {
      clear: 'Sightlines are clear enough that every movement can be read and answered.',
      limited: 'Broken sightlines keep hiding the next few seconds of the chase.',
      poor: 'Shapes appear only when they are already close enough to matter.',
      'almost-none': 'You are running through near blindness, guided by sound, touch, and whatever memory survives the panic.'
    };

    const UNCERTAINTY_NARRATION = {
      low: 'The route is difficult, but its logic can still be understood.',
      medium: 'The route keeps introducing new variables faster than anyone can control them.',
      high: 'Nothing stays predictable for more than a few heartbeats, including the direction of the pursuit.'
    };

    const TRAIT_MATRIX = {
      crowded: {
        read: 'Bodies, carts, and frightened bystanders turn every direct line into a moving obstacle.',
        dm: 'Crowds create witnesses, collateral risk, and social approaches.',
        cost: 'A witness gets a clear look at you and begins shouting a description down the route.',
        setback: 'The crowd closes around the confusion, separating the fastest character from the safest path.'
      },
      dark: {
        read: 'Light reaches only far enough to reveal the next mistake.',
        dm: 'Darkness rewards perception, light management, stealth, and deliberate risk.',
        cost: 'Your light is knocked away and vanishes behind you, leaving the next stretch harder to read.',
        setback: 'For several seconds, you lose both the route and one another in the dark.'
      },
      vertical: {
        read: 'The route climbs, drops, and folds over open space instead of remaining on one level.',
        dm: 'Vertical terrain introduces falling risk, divided elevations, and rope or flight solutions.',
        cost: 'The crossing leaves one of you on the wrong level, still moving but no longer protected by the group.',
        setback: 'The route drops away beneath the last character, forcing a desperate lower path.'
      },
      fragile: {
        read: 'Every impact travels through the structure, loosening something that was already close to failure.',
        dm: 'Fragile terrain turns force and speed into useful but dangerous choices.',
        cost: 'The route gives way behind you, but not cleanly; debris and dust erase the easiest path forward as well.',
        setback: 'The structure fails early and carries part of the route down with it.'
      },
      flooded: {
        read: 'Cold water drags at every step and hides the shape of the ground beneath it.',
        dm: 'Flooding threatens equipment, footing, breath, and characters burdened by weight.',
        cost: 'The water tears something useful loose and carries it back toward the pursuit.',
        setback: 'A sudden drop takes one character off their feet and gives the opposition the distance it needed.'
      },
      burning: {
        read: 'Heat bends the air while smoke turns every breath into a decision.',
        dm: 'Fire pressures time, visibility, route choice, and the protection of vulnerable people.',
        cost: 'The route remains open, but flame cuts off the way back and leaves no second attempt.',
        setback: 'A burst of heat forces the group apart and drives someone onto a worse line.'
      },
      'secret-passages': {
        read: 'The walls offer too many seams, false turns, and hidden ways for either side to exploit.',
        dm: 'Secret routes reward investigation and allow the opposition to intercept rather than follow.',
        cost: 'The hidden route closes behind you before everyone can use it cleanly.',
        setback: 'A concealed opening puts the opposition on a parallel route with a shorter line to the objective.'
      },
      guarded: {
        read: 'Every crossing point carries the risk of another alarm, another gate, and another pair of eyes.',
        dm: 'Guards create social, stealth, disguise, and alarm-management choices.',
        cost: 'An alarm spreads ahead of you, making the next checkpoint ready before you arrive.',
        setback: 'Fresh defenders enter the route and turn the chase into a coordinated interception.'
      },
      noisy: {
        read: 'Metal, stone, water, or machinery announces every movement farther than sight can follow.',
        dm: 'Noise makes speed visible and enables diversions or false trails.',
        cost: 'The route broadcasts exactly where you are, even though you keep moving.',
        setback: 'The opposition reads the noise correctly and cuts toward your next position.'
      },
      unstable: {
        read: 'The ground shifts under pressure, changing the route even while you cross it.',
        dm: 'Instability supports balance, survival, engineering, and sacrificial route choices.',
        cost: 'The terrain settles badly after you pass, leaving the slowest character without the same footing.',
        setback: 'The route shifts at the worst moment and carries you away from the intended line.'
      },
      cramped: {
        read: 'The route compresses into passages where shoulders, weapons, and carried gear catch against every turn.',
        dm: 'Cramped terrain rewards small size, flexible movement, tools, and choices about what must be abandoned.',
        cost: 'You force a way through, but leave equipment or formation behind in the narrow passage.',
        setback: 'The route pinches shut around the group and lets the opposition threaten both ends at once.'
      },
      exposed: {
        read: 'There is nowhere to hide movement, hesitation, or the exact line you intend to take.',
        dm: 'Exposed terrain rewards raw speed, cover creation, ranged interference, and deliberate diversions.',
        cost: 'You cross the open ground, but the opposition learns exactly where you are heading.',
        setback: 'The open route gives the opposition a clean line to intercept or attack.'
      },
      slippery: {
        read: 'Every surface offers less grip than it should, turning speed into a wager against balance.',
        dm: 'Slippery terrain favors balance, anchors, ropes, careful route choice, and environmental control.',
        cost: 'You stay upright, but lose an item or secure footing for the next move.',
        setback: 'A bad slide carries one character away from the intended line and into immediate danger.'
      },
      'difficult-terrain': {
        read: 'Broken ground, debris, roots, or rubble consume every step and punish anyone who chooses a straight line.',
        dm: 'Difficult terrain rewards route reading, mobility features, teamwork, and clearing a path.',
        cost: 'You keep moving, but the slowest character spends more energy than the group can easily recover.',
        setback: 'The terrain strips away momentum and lets the opposition dictate the next route.'
      },
      submerged: {
        read: 'Part of the route disappears beneath water, forcing movement, breath, and orientation into the same decision.',
        dm: 'Submerged terrain pressures breath, swimming, equipment, visibility, and rescue choices.',
        cost: 'You surface on the correct side, but something important remains below or drifts away.',
        setback: 'One character loses direction underwater and emerges separated from the safest route.'
      },
      freezing: {
        read: 'Cold stiffens hands, steals breath, and turns every wet or exposed surface into a new hazard.',
        dm: 'Freezing conditions reward preparation, endurance, fire, shelter, and fast decisive movement.',
        cost: 'You cross, but numb hands or frozen gear make the next action slower and less certain.',
        setback: 'The cold locks up movement at the worst moment and gives the opposition room to close.'
      },
      toxic: {
        read: 'The air, water, or ground carries something that burns the lungs and punishes every second of exposure.',
        dm: 'Toxic terrain pressures Constitution, protective magic, breath management, and route speed.',
        cost: 'You pass through the contamination, but someone carries its effects into the next beat.',
        setback: 'The exposure forces the group to stop, split, or choose a more dangerous route for clean air.'
      },
      'strong-winds': {
        read: 'Wind tears at balance, speech, loose equipment, and any route that leaves the ground.',
        dm: 'Strong winds reward anchors, low routes, communication, ranged caution, and control magic.',
        cost: 'You cross, but the wind takes an item, signal, or stable formation with it.',
        setback: 'A violent gust throws one character off line and opens the route to the opposition.'
      },
      'moving-machinery': {
        read: 'Gears, lifts, belts, pistons, or rotating structures keep changing which parts of the route exist.',
        dm: 'Moving machinery rewards timing, tools, Arcana, Investigation, and deliberate interference.',
        cost: 'The mechanism carries you through, but jams behind you in a way that also blocks the clean path ahead.',
        setback: 'The machinery changes phase too early and traps part of the group on the wrong side.'
      },
      traffic: {
        read: 'Vehicles, mounts, carts, or other fast-moving bodies cross the chase at angles no one controls.',
        dm: 'Traffic rewards anticipation, social command, acrobatics, and turning movement against the opposition.',
        cost: 'You clear the crossing, but cause a collision or disturbance that identifies your route.',
        setback: 'The traffic divides the group and gives the opposition a faster lane.'
      },
      overgrown: {
        read: 'Roots, thorn, vines, and dense growth hide the ground while pulling at clothing and equipment.',
        dm: 'Overgrowth rewards Survival, cutting tools, small routes, fire risk, and animal knowledge.',
        cost: 'You force through, but leave a clear trail and something torn loose behind you.',
        setback: 'The growth closes around one character and turns the route into an extraction problem.'
      },
      trapped: {
        read: 'Pressure plates, wires, runes, deadfalls, or suspiciously clean gaps make speed itself dangerous.',
        dm: 'Trapped terrain rewards perception, tools, sacrifice, remote interaction, and controlled risk.',
        cost: 'You bypass the trap, but arm or redirect it in a way that threatens the next person through.',
        setback: 'The trap triggers and changes the chase into a rescue, separation, or containment problem.'
      },
      'civilian-risk': {
        read: 'People who cannot defend themselves are caught inside the route, turning the fastest answer into a moral choice.',
        dm: 'Civilian risk rewards protection, command, restraint, and accepting costs to keep others safe.',
        cost: 'You protect the civilians, but surrender distance or reveal your identity doing it.',
        setback: "The route becomes a rescue scene as the opposition exploits the group's refusal to abandon innocent people."
      },
      collapsing: {
        read: 'The environment is not merely unstable; it is coming apart now, taking routes and landmarks with it.',
        dm: 'Active collapse pressures timing, sacrifice, protection, route memory, and one-way decisions.',
        cost: 'You pass before the collapse, but the route ahead is cut off from any retreat or reinforcement.',
        setback: 'The collapse divides the chase into separate paths and leaves someone on the wrong side.'
      },
      'magically-warped': {
        read: 'Magic has bent distance, weight, or direction into something that only resembles ordinary terrain.',
        dm: 'Magical distortion rewards arcane knowledge, intuition, counter-magic, and imaginative rules interactions.',
        cost: 'The place lets you pass, but leaves a visible magical mark that the opposition can follow.',
        setback: 'Distance folds against you, returning part of the group to a position it had already escaped.'
      },
      'rough-seas': {
        read: 'The surface rises and drops hard enough to hide vessels, break footing, and turn every maneuver into a contest with the water.',
        dm: 'Rough seas pressure balance, water vehicles, rigging, hull integrity, spell concentration, and rescue.',
        cost: 'The craft keeps its line, but crew, cargo, rigging, or passengers are thrown out of position.',
        setback: 'A wave turns the vessel broadside, floods a section, or exposes it to collision and boarding.'
      },
      'strong-current': {
        read: 'The current keeps choosing a direction for everyone, whether or not it matches the objective.',
        dm: 'Strong current rewards route reading, anchors, swimming, water vehicles, ropes, and controlled use of magic.',
        cost: 'The party crosses the current but emerges farther from the intended line or without something carried.',
        setback: 'The current separates one participant and delivers the opposition a faster channel.'
      },
      'reefs-shallows': {
        read: 'Dark shapes beneath the surface mark reefs, wreckage, sandbars, or shallows that punish the wrong draft and angle.',
        dm: 'Reefs and shallows create navigation choices, grounding risk, hidden channels, smaller-craft advantages, and hull damage.',
        cost: 'The vessel clears the hazard but scrapes, loses speed, or leaves a visible wake through the safe channel.',
        setback: 'The craft grounds or tears open enough hull to make repair part of the chase.'
      },
      'loose-cargo': {
        read: 'Unsecured cargo shifts with every turn, changing balance and threatening anyone caught on the wrong side.',
        dm: 'Loose cargo can be secured, sacrificed, weaponized, hidden inside, or used to change weight and handling.',
        cost: 'The maneuver works, but valuable cargo breaks loose or blocks an important station.',
        setback: 'The cargo slams into crew, passengers, controls, or the vehicle itself and steals the next clean action.'
      },
      'frightened-mounts': {
        read: 'The mounts feel the danger before their riders agree on what it is, and their movement begins to fracture.',
        dm: 'Frightened mounts reward Animal Handling, calming magic, formation changes, dismounting, and use of herd instinct.',
        cost: 'The mounts obey, but one becomes exhausted, injured, or unwilling to repeat the maneuver.',
        setback: 'A mount bolts, rears, throws its rider, or carries the chase onto an uncontrolled line.'
      },
      'aerial-traffic': {
        read: 'Other flyers, airships, bridges, cranes, banners, or migrating creatures occupy the space between both sides.',
        dm: 'Aerial traffic creates collision, cover, drafting, social signaling, and rapid altitude-change opportunities.',
        cost: 'The party threads the traffic but causes a collision, alarm, or witness problem behind it.',
        setback: 'The traffic divides formation and gives the opposition a clean interception vector.'
      },
      'low-ceiling': {
        read: 'The airspace compresses until climbing, diving, turning, and even spreading wings become dangerous.',
        dm: 'Confined airspace rewards precision, braking, small forms, crawling routes, landing, and environmental control.',
        cost: 'The group passes through but loses speed, formation, or clearance for the next maneuver.',
        setback: 'A flyer strikes the ceiling or is forced to land while the opposition remains mobile.'
      },
      'hazardous-weather': {
        read: 'Rain, hail, lightning, sand, snow, or violent wind changes visibility and movement faster than either side can adapt.',
        dm: 'Hazardous weather affects navigation, concentration, mounts, vehicles, sails, flight, exposure, and communication.',
        cost: 'The party keeps control but loses sight, equipment, endurance, or a reliable bearing.',
        setback: 'The weather forces an emergency course change and hands the opposition the better line.'
      }
    };

    const OPPOSITION_SCALE_MATRIX = {
      one: {
        escaping: 'Only one pursuer is involved, making every movement deliberate, personal, and difficult to lose in a crowd.',
        pursuing: 'Your quarry is a single moving point, free to choose openings too narrow or dangerous for a group.'
      },
      pair: {
        escaping: 'Two pursuers work the route from different angles, one maintaining pressure while the other looks ahead.',
        pursuing: 'The two targets can separate, cover one another, or force you to choose which trail matters.'
      },
      few: {
        escaping: 'Several pursuers are close enough to split around obstacles and threaten more than one route.',
        pursuing: 'Several targets are moving together, giving them enough bodies to misdirect, delay, and protect the slowest among them.'
      },
      group: {
        escaping: 'A full group is behind you, large enough to occupy parallel paths and keep pressure on every mistake.',
        pursuing: 'The fleeing group can divide across the route, forcing you to manage distance rather than follow one obvious trail.'
      },
      crowd: {
        escaping: 'The pursuit has become a crowd, more dangerous through numbers, noise, and the space it can deny.',
        pursuing: 'The quarry moves as a crowd, able to scatter into the environment and turn identification into part of the chase.'
      },
      horde: {
        escaping: 'The route is filling with a horde; you are not merely outrunning individuals, but the space their numbers consume.',
        pursuing: 'The quarry is a moving mass, too numerous to contain cleanly and capable of leaving chaos in every direction.'
      }
    };

    const OPPOSITION_TYPE_MATRIX = {
      humanoids: {
        escaping: 'Shouted signals and deliberate choices make it clear that intelligence, not instinct, is guiding the pursuit.',
        pursuing: 'The trail contains feints, signals, and choices meant to be interpreted incorrectly.'
      },
      beast: {
        escaping: 'Breath, claws, scent, and instinct keep the pursuit fixed on you even when sightlines break.',
        pursuing: 'The target reads terrain through instinct, scent, and openings no civilized route was built to contain.'
      },
      monster: {
        escaping: 'Something too powerful or unnatural to move like an ordinary hunter is forcing the route to accommodate it.',
        pursuing: 'The target crosses the environment according to its own anatomy and appetite rather than ordinary movement.'
      },
      undead: {
        escaping: 'The pursuit never wastes breath, never argues with pain, and never slows for reasons the living understand.',
        pursuing: 'The target leaves no signs of fatigue and treats dangerous terrain as though discomfort were irrelevant.'
      },
      construct: {
        escaping: 'The rhythm behind you never changes; the pursuit advances with mechanical certainty.',
        pursuing: 'The target follows a pattern without hesitation, but the pattern may be more precise than it first appears.'
      },
      magical: {
        escaping: 'The pressure moves like a spell or living force, ignoring parts of the route that should matter to a physical pursuer.',
        pursuing: 'The target alters distance, direction, or access through magic instead of ordinary speed.'
      },
      other: {
        escaping: 'The nature of the pursuit changes how it reads the route and what it is willing to ignore.',
        pursuing: 'The target moves according to rules the party must identify while the distance keeps changing.'
      }
    };

    const DIFFICULTY_NARRATION = {
      forgiving: 'The route offers more than one viable answer, and a good idea should matter even before the roll.',
      standard: 'The route is demanding but readable, with room for several sensible approaches.',
      challenging: 'The opening is narrow enough that timing, teamwork, or a strong resource will matter.',
      brutal: 'The route offers no comfortable answer; success will require commitment, sacrifice, or exceptional execution.'
    };

    const STARTING_POSITION_NARRATION = {
      '-2': 'The chase begins with the party already badly out of position and almost no distance to spend.',
      '-1': 'The chase begins slightly against the party, with the opposition already controlling the cleaner line.',
      '0': 'Both sides enter the chase with enough room to change who controls it.',
      '1': 'The party begins with a small but real positional advantage.',
      '2': 'The party begins with a strong lead, though the opposition is already searching for a way to erase it.'
    };

    const BEHAVIOR_MATRIX = {
      fast: {
        escaping: '{opposition} does not slow to secure the route; it spends everything on closing the distance now.',
        pursuing: '{opposition} abandons caution and commits to raw speed.'
      },
      relentless: {
        escaping: '{opposition} keeps coming through every hazard, accepting damage rather than losing ground.',
        pursuing: '{opposition} never truly sprints, but never gives you a moment in which the distance stops growing.'
      },
      clever: {
        escaping: '{opposition} stops following the obvious line and begins looking for the route that reaches you first.',
        pursuing: '{opposition} leaves false signs and chooses turns designed to make you commit before you understand them.'
      },
      brutal: {
        escaping: '{opposition} smashes through the route instead of solving it, turning obstacles into debris behind you.',
        pursuing: '{opposition} tears through anything that might slow it, leaving damage and panic as a trail.'
      },
      coordinated: {
        escaping: '{opposition} moves in separate elements, one pressing from behind while another works to close the next exit.',
        pursuing: '{opposition} uses allies, signals, or prepared positions to keep you from gaining a clean line.'
      },
      unpredictable: {
        escaping: '{opposition} changes pace and direction without warning, making its next move impossible to read for long.',
        pursuing: '{opposition} keeps changing methods, sacrificing the safest route whenever a stranger one appears.'
      }
    };

    const ROLE_MATRIX = {
      agile: { check: 'Dexterity (Acrobatics)', approach: 'Use speed, balance, and body control to take the most direct line.', read: '{character} sees a line through the obstacle that exists only for someone quick enough to use it.' },
      stealthy: { check: 'Dexterity (Stealth)', approach: 'Break line of sight, misdirect attention, or disappear into the route.', read: '{character} notices the brief moment when movement can become disappearance.' },
      strong: { check: 'Strength (Athletics)', approach: 'Force, lift, hold, climb, or reshape the obstacle for the group.', read: '{character} can turn raw force into a route before the opening closes.' },
      resilient: { check: 'Constitution', approach: 'Endure exposure, pain, exhaustion, or hostile terrain long enough to keep moving.', read: '{character} is the one person who might absorb the route\'s punishment without surrendering momentum.' },
      observant: { check: 'Wisdom (Perception)', approach: 'Notice the opening, hidden threat, or change in the opposition before it becomes obvious.', read: '{character} catches the detail that reveals what the route is about to do.' },
      social: { check: 'Charisma', approach: 'Direct, deceive, intimidate, or recruit the people inside the chase.', read: '{character} can change the behavior of everyone blocking the route before force becomes necessary.' },
      arcane: { check: 'Intelligence (Arcana)', approach: 'Read or alter the magical, structural, or energetic logic of the obstacle.', read: '{character} recognizes a pattern in the danger that can be answered with arcane knowledge.' },
      divine: { check: 'Wisdom or divine magic', approach: 'Protect the vulnerable, steady the group, or answer supernatural pressure.', read: '{character} feels where protection, conviction, or divine power could keep the group together.' },
      explorer: { check: 'Wisdom (Survival)', approach: 'Read terrain, choose the viable line, and avoid the route the opposition expects.', read: '{character} reads the route as a trail rather than a wall.' },
      support: { check: 'Aid or a relevant support ability', approach: 'Create the opening another character needs, remove a complication, or protect the slowest member.', read: '{character} can make someone else\'s impossible move possible.' },
      controller: { check: 'A control effect or relevant spellcasting ability', approach: 'Change movement, space, access, or the opposition\'s options.', read: '{character} can reshape the chase itself instead of merely running through it.' },
      ranged: { check: 'A ranged attack or Dexterity check', approach: 'Interact with a distant mechanism, cover the route, or force the opposition to hesitate.', read: '{character} has a clear moment to affect something no one can safely reach.' }
    };

    const CONSEQUENCE_MATRIX = {
      adventurous: {
        pressure: 'Consequences should be vivid, inconvenient, and fun rather than permanently damaging.',
        genericCost: 'Something useful is dropped, broken, or left in an embarrassingly visible place.',
        genericSetback: 'The chase erupts into a messier route, but no one is removed from play.'
      },
      serious: {
        pressure: 'Consequences can expose identities, consume resources, divide positions, or create a lasting complication.',
        genericCost: 'You keep moving, but lose a resource, clean route, or piece of secrecy that will matter later.',
        genericSetback: 'The opposition gains a meaningful positional advantage and forces the next decision under pressure.'
      },
      severe: {
        pressure: 'Consequences may include injury, capture of one character, loss of an important object, or damage to the objective.',
        genericCost: 'The route exacts a real price: blood, equipment, position, or the safety of someone you meant to protect.',
        genericSetback: 'One part of the plan fails hard enough to create a new emergency inside the chase.'
      },
      deadly: {
        pressure: 'Consequences may create lethal exposure or a desperate last stand, but never erase the story without a playable decision.',
        genericCost: 'You survive the obstacle, but it leaves someone exposed to a danger that must be answered immediately.',
        genericSetback: 'The chase reaches a potentially lethal turning point: separation, a fall, capture, or an unavoidable confrontation.'
      }
    };

    const ENVIRONMENT_SCENES = {
      village: [
        {
          type: 'Split-second obstacle',
          prompt: 'A produce cart overturns across the lane as shutters slam and frightened villagers scatter in every direction.',
          note: 'The obstacle can be crossed, redirected, or turned against the opposition. Reward solutions that protect bystanders.',
          check: 'Clear the blocked lane',
          instruction: 'Ask one character to lead the response while another may assist through a relevant action.',
          approaches: [
            ['Dexterity (Acrobatics)', 'Vault the cart and keep moving.'],
            ['Strength (Athletics)', 'Drag or smash a path through.'],
            ['Charisma', 'Direct the crowd before panic seals the route.']
          ]
        },
        {
          type: 'Route decision',
          prompt: 'The road forks around a crowded shrine. One path is wider but exposed; the other disappears through laundry lines and animal pens.',
          note: 'Neither path is wrong. The exposed route favors speed; the cramped route favors deception and improvisation.',
          check: 'Choose and exploit the better route',
          instruction: 'Let the players describe the route and method, then call for the most relevant check.',
          approaches: [
            ['Wisdom (Perception)', 'Read movement and spot the faster opening.'],
            ['Dexterity (Stealth)', 'Break line of sight through the narrow route.'],
            ['Intelligence', 'Predict where the opposition expects them to go.']
          ]
        }
      ],
      city: [
        {
          type: 'Crowd challenge',
          prompt: 'A bell rings somewhere above the roofs. The street ahead floods with carts, hawkers, watch patrols, and people who have no idea they are standing inside a chase.',
          note: 'A loud or violent solution is fast but increases peril. A subtle solution may preserve anonymity.',
          check: 'Move through the crowd without losing momentum',
          instruction: 'Choose the character whose strengths best match the party plan.',
          approaches: [
            ['Charisma (Persuasion)', 'Create a moving corridor through the crowd.'],
            ['Dexterity (Acrobatics)', 'Use stalls, carts, and railings as a route.'],
            ['Wisdom (Insight)', 'Anticipate the crowd before gaps close.']
          ]
        }
      ],
      'urban-district': [
        {
          type: 'Vertical route',
          prompt: 'The street ends at a locked gate, but balconies, drainpipes, and a sagging awning form a dangerous path above it.',
          note: 'Failure should separate, expose, or cost resources before it simply stops movement.',
          check: 'Take the chase above street level',
          instruction: 'Ask who goes first and how the rest follow.',
          approaches: [
            ['Strength (Athletics)', 'Climb directly and secure a route.'],
            ['Dexterity (Acrobatics)', 'Cross the unstable awning at speed.'],
            ['Tools or magic', 'Create an improvised ascent.']
          ]
        }
      ],
      dungeon: [
        {
          type: 'Collapsing route',
          prompt: 'The corridor ahead is already failing. Mortar spits from the ceiling, flagstones tilt, and the next impact behind the party shakes loose an entire arch.',
          note: 'The route is dangerous but still usable. A setback can cost equipment, split positions, or raise peril instead of halting the chase.',
          check: 'Cross before the passage gives way',
          instruction: 'Ask one character to lead the crossing. Another character may protect the slowest party member.',
          approaches: [
            ['Dexterity (Acrobatics)', 'Keep balance across shifting stone.'],
            ['Strength (Athletics)', 'Hold or brace the failing structure.'],
            ['Intelligence (Investigation)', 'Identify the safest sequence of steps.']
          ]
        },
        {
          type: 'Unknown mechanism',
          prompt: 'A sealed stone door blocks the passage. Beside it, three worn levers sit beneath symbols almost erased by age.',
          note: 'Pulling a lever is a meaningful choice, not a quiz with one correct answer. Each option should open a route with a different complication.',
          check: 'Open a route before the opposition arrives',
          instruction: 'Let the party inspect, force, bypass, or deliberately gamble on the mechanism.',
          approaches: [
            ['Intelligence (Investigation)', 'Read wear patterns and mechanism logic.'],
            ['Strength (Athletics)', 'Force the door before the mechanism matters.'],
            ['Thieves\' tools or magic', 'Bypass the intended controls.']
          ]
        },
        {
          type: 'Noise and pursuit',
          prompt: 'The chamber ahead is filled with hanging chains and cracked bronze vessels. Crossing quietly looks slow; crossing quickly will announce every step.',
          note: 'Speed and stealth create different stories. A loud success can grant advantage while also increasing peril.',
          check: 'Cross without surrendering the initiative',
          instruction: 'Ask the players whether they value silence or speed, then select the check that follows from their plan.',
          approaches: [
            ['Dexterity (Stealth)', 'Thread through without touching the hanging debris.'],
            ['Dexterity (Acrobatics)', 'Move fast enough that the noise does not matter.'],
            ['Creative diversion', 'Make the noise happen somewhere else.']
          ]
        }
      ],
      castle: [
        {
          type: 'Closing barrier',
          prompt: 'A portcullis drops ahead while armored figures rush to lock the winch. There is still space beneath it, but not for long.',
          note: 'A character can cross, hold the gate, attack the mechanism, or redirect the chase through a side route.',
          check: 'Beat or defeat the closing gate',
          instruction: 'Choose the character taking the greatest immediate risk.',
          approaches: [
            ['Dexterity (Acrobatics)', 'Slide through before the gap closes.'],
            ['Strength (Athletics)', 'Hold the gate long enough for everyone.'],
            ['Attack, tools, or magic', 'Disable the winch or locking mechanism.']
          ]
        }
      ],
      labyrinth: [
        {
          type: 'Orientation challenge',
          prompt: 'The same carved face appears at the next junction, then again at the next. Behind the party, the pursuit no longer sounds as if it is following the same corridors.',
          note: 'The labyrinth should change relative position, not erase all progress. A setback can put the opposition on an intersecting route.',
          check: 'Choose a route that changes the chase',
          instruction: 'Ask what clues the party trusts: sound, airflow, markings, memory, magic, or instinct.',
          approaches: [
            ['Wisdom (Survival)', 'Track direction through subtle physical clues.'],
            ['Intelligence (Investigation)', 'Recognize the pattern behind repeated junctions.'],
            ['Wisdom (Perception)', 'Locate movement through walls and echoes.']
          ]
        }
      ],
      caverns: [
        {
          type: 'Natural hazard',
          prompt: 'The ledge narrows above a black underground river. Water has polished the stone smooth, and something heavy is moving through the tunnel behind you.',
          note: 'Falling need not remove a character from play. It can create a parallel route, cost gear, or force an immediate rescue.',
          check: 'Cross the wet ledge under pressure',
          instruction: 'Ask who crosses first and who watches the opposition.',
          approaches: [
            ['Dexterity (Acrobatics)', 'Keep balance and move quickly.'],
            ['Strength (Athletics)', 'Anchor a rope or carry another character.'],
            ['Wisdom (Perception)', 'Find handholds hidden by darkness and spray.']
          ]
        }
      ],
      forest: [
        {
          type: 'Broken terrain',
          prompt: 'Roots tear through the ground like ribs. The direct route plunges into thorn and deadfall, while a game trail bends away from the objective.',
          note: 'The direct route costs safety. The longer route costs time. Both can work.',
          check: 'Keep direction through hostile terrain',
          instruction: 'Let the party choose speed, concealment, or certainty.',
          approaches: [
            ['Wisdom (Survival)', 'Take the trail without losing the objective.'],
            ['Strength (Athletics)', 'Break through the direct route.'],
            ['Dexterity (Stealth)', 'Use dense cover to change relative position.']
          ]
        }
      ],
      mountains: [
        {
          type: 'Exposed crossing',
          prompt: 'The path crosses a slope of loose shale above a sheer drop. Every hurried step sends stones rattling into the dark below.',
          note: 'A setback can trigger a slide, damage supplies, or leave someone hanging rather than causing an arbitrary fall to death.',
          check: 'Cross without losing control of the slope',
          instruction: 'Choose the character setting the pace or securing the party.',
          approaches: [
            ['Dexterity (Acrobatics)', 'Move lightly across unstable ground.'],
            ['Strength (Athletics)', 'Secure others with rope and force.'],
            ['Wisdom (Survival)', 'Read the stable line across the slope.']
          ]
        }
      ],
      swamp: [
        {
          type: 'Hidden ground',
          prompt: 'The path dissolves into reeds and black water. One step finds mud, the next finds nothing, and ripples spread from somewhere the party did not disturb.',
          note: 'Use the environment as active pressure. A setback can slow, stain, reveal, or attract rather than merely deal damage.',
          check: 'Find a route through the flooded ground',
          instruction: 'Ask whether the party tests the path, commits to speed, or creates its own route.',
          approaches: [
            ['Wisdom (Survival)', 'Read water, reeds, and submerged ground.'],
            ['Strength (Athletics)', 'Force through mud and vegetation.'],
            ['Intelligence (Nature)', 'Identify stable growth and dangerous water.']
          ]
        }
      ],
      river: [
        {
          type: 'Water crossing',
          prompt: 'The route ends at fast water. A damaged ferry line hangs across the current, and the nearest bridge is already filling with movement.',
          note: 'Crossing, using the current, damaging the bridge, or commandeering a boat are all valid directions.',
          check: 'Turn the water into a route instead of a dead end',
          instruction: 'Ask what the party is willing to risk or abandon to cross quickly.',
          approaches: [
            ['Strength (Athletics)', 'Swim, pull, or control a line through the current.'],
            ['Dexterity (Acrobatics)', 'Cross using the damaged ferry rig.'],
            ['Vehicles, tools, or magic', 'Create or seize another method of crossing.']
          ]
        }
      ],
      sewers: [
        {
          type: 'Constricted route',
          prompt: 'The walkway disappears beneath a low tunnel where foul water nearly reaches the ceiling. A rusted maintenance hatch offers another route, but its wheel will not turn.',
          note: 'Both routes create different complications: exposure in the water or delay at the hatch.',
          check: 'Choose and force a route through the obstruction',
          instruction: 'Let the players choose between the submerged passage and the locked hatch.',
          approaches: [
            ['Constitution', 'Push through the submerged passage.'],
            ['Strength (Athletics)', 'Break the hatch wheel free.'],
            ['Thieves\' tools or magic', 'Open the maintenance route cleanly.']
          ]
        }
      ],
      ruins: [
        {
          type: 'Unstable shortcut',
          prompt: 'A fallen wall forms a ramp toward the objective, but the stones shift under the first touch. The safe route winds through exposed ground below.',
          note: 'The shortcut should be genuinely faster and genuinely dangerous.',
          check: 'Exploit the unstable shortcut',
          instruction: 'Ask who tests the route and how the party prevents a collapse.',
          approaches: [
            ['Dexterity (Acrobatics)', 'Cross before the stones settle.'],
            ['Intelligence (Investigation)', 'Identify load-bearing points.'],
            ['Strength (Athletics)', 'Stabilize or reshape the ramp.']
          ]
        }
      ],
      planar: [
        {
          type: 'Reality shift',
          prompt: 'The horizon folds. A doorway appears beneath the party, while the path behind stretches into a distance that did not exist a moment ago.',
          note: 'The scene should challenge assumptions, not demand knowledge of a secret answer.',
          check: 'Move through changing reality without losing the objective',
          instruction: 'Ask what principle the characters trust when distance and direction stop behaving normally.',
          approaches: [
            ['Intelligence (Arcana)', 'Read the logic of the distortion.'],
            ['Wisdom (Insight)', 'Recognize what the place is trying to provoke.'],
            ['Force of will or magic', 'Impose a temporary path through the change.']
          ]
        }
      ],
      custom: [
        {
          type: 'Environmental complication',
          prompt: 'The route ahead changes without warning, forcing the party to choose between speed, safety, and keeping the opposition in sight.',
          note: 'Adapt the obstacle to the custom environment. Preserve forward motion even when the party pays a cost.',
          check: 'Exploit the changing route',
          instruction: 'Ask the players to describe an approach grounded in the environment they defined.',
          approaches: [
            ['Relevant physical skill', 'Overcome the immediate terrain.'],
            ['Relevant knowledge skill', 'Understand the environment before committing.'],
            ['Equipment, magic, or teamwork', 'Create a new option.']
          ]
        }
      ]
    };

    const GENERIC_SCENES = [
      {
        type: 'Spotlight moment',
        prompt: '{character} sees the danger first: the route ahead is closing, and the party has only a heartbeat to decide who commits to the opening.',
        note: 'Give the named character the first decision, not the entire burden. Let another character assist or complicate the plan.',
        check: 'Seize the brief opening',
        instruction: 'Ask {character} what they do before explaining every possible consequence.',
        approaches: [
          ['Best character strength', 'Use a defining capability to create momentum.'],
          ['Teamwork', 'Coordinate two characters around the opening.'],
          ['Resource use', 'Spend equipment, magic, or position to avoid the check.']
        ]
      },
      {
        type: 'Opposition move',
        prompt: '{opposition} stops following the obvious route. A sound, shadow, or sudden silence reveals an attempt to cut the party off instead.',
        note: 'The opposition is acting intelligently. A setback changes relative position rather than invalidating previous success.',
        check: 'Read and counter the interception',
        instruction: 'Ask how the party recognizes the maneuver and whether it changes direction or confronts it.',
        approaches: [
          ['Wisdom (Perception)', 'Detect the interception before it closes.'],
          ['Intelligence (Investigation)', 'Predict the opposition route.'],
          ['Deception or diversion', 'Feed the opposition a false position.']
        ]
      },
      {
        type: 'Costly opportunity',
        prompt: 'A dangerous shortcut opens toward the objective, but taking it means leaving something exposed: supplies, secrecy, position, or a vulnerable ally.',
        note: 'State the visible cost honestly. Do not hide a punishment behind a choice presented as safe.',
        check: 'Claim the shortcut without surrendering too much',
        instruction: 'Ask what the party protects and what it is willing to risk.',
        approaches: [
          ['Dexterity or Strength', 'Take the route at physical risk.'],
          ['Intelligence or Wisdom', 'Reduce the cost before committing.'],
          ['Deliberate sacrifice', 'Accept a narrative cost and gain automatic progress.']
        ]
      }
    ];

    const ENVIRONMENT_KITS = {
      village: {
        landmarks: [
          'The lane narrows between a flour-dusted bakery and a cooper\'s yard',
          'A shrine square opens around an ancient linden tree hung with ribbons',
          'The chase bursts beside a millrace where a turning wheel shakes the timber walkway',
          'A row of low cottages leans over a drainage ditch crossed by loose planks',
          'The road bends around a crowded inn yard full of tethered mounts and stacked barrels',
          'A small stone bridge pinches the village road above a fast irrigation channel'
        ],
        obstacles: [
          'A delivery wagon jackknifes across the cobbles, spilling hoops, flour sacks, and a furious driver into the only clear lane',
          'A flock of geese erupts from an open pen and turns the square into a wall of wings, noise, and snapping beaks',
          'A funeral procession rounds the corner beneath a long black canopy, leaving no respectful way through at full speed',
          'A panicked mule tears free from its hitch and drags half a market stall behind it',
          'The miller drops the sluice gate, and water begins pouring across the stepping stones ahead',
          'A wedding party spills from the inn carrying benches, ribbons, trays, and absolutely no awareness of the chase'
        ],
        opportunities: [
          'The bakery door stands open, revealing a hot kitchen, a trapdoor to the flour cellar, and a rear yard beyond',
          'A hay wagon is beginning to roll downhill with no one holding the reins',
          'A ladder rests against the inn roof beside a line of connected sheds',
          'The irrigation channel disappears beneath a grating wide enough for one person at a time',
          'A carpenter\'s half-built cart offers wheels, rope, planks, and a frame light enough to overturn',
          'The shrine tree\'s heavy festival ropes reach from the branches to posts across the square'
        ],
        wildcards: [
          'Three stray dogs begin barking at everyone who runs, then choose a side with no obvious logic',
          'A child recognizes one of the characters and shouts their name across the square',
          'A bellringer mistakes the commotion for a fire and starts sounding the alarm',
          'A beekeeper carrying an open frame freezes in the center of the lane',
          'A farmer opens a livestock gate at exactly the wrong moment',
          'A village elder steps into the road and demands to know what is happening'
        ],
        sensory: ['warm bread and woodsmoke', 'mud, feathers, and ringing bells', 'millwater and wet timber', 'sunlit dust and frightened voices']
      },
      city: {
        landmarks: [
          'The street drops into a packed market beneath balconies draped with dyed cloth',
          'A broad avenue funnels toward a triumphal arch watched by mounted patrols',
          'The chase reaches a canal quarter stitched together by stone bridges and loading cranes',
          'A public bathhouse courtyard opens between tiled roofs and steaming vents',
          'The road squeezes through a night market lit by oil lamps and mirrored shopfronts',
          'A monumental stair climbs around fountains, statues, and a constant stream of pedestrians'
        ],
        obstacles: [
          'A glassmaker\'s cart clips a bollard and bursts across the street in glittering sheets and rolling iron frames',
          'A sedan chair procession stops sideways beneath the arch while its guards argue with a tax inspector',
          'A crane operator swings a cargo net full of amphorae directly across the nearest bridge',
          'A watch patrol begins lowering a chain barrier after hearing alarms from the previous street',
          'A troupe of masked performers floods the square in identical costumes, making faces and targets impossible to track',
          'A fishmonger\'s awning tears loose and wraps itself around two stalls as the crowd surges away'
        ],
        opportunities: [
          'An open dye shop leads through hanging cloth, vats, and a delivery door onto the next street',
          'A scaffold climbs the face of an unfinished guildhall and connects to three adjacent roofs',
          'A flat-bottomed cargo boat is pushing away from the canal steps with one mooring line still attached',
          'A courier leaves a fast horse unattended beside a public fountain',
          'A storm drain grate has been lifted for repairs, exposing a maintenance passage below the avenue',
          'A bell tower door stands ajar, with stairs that emerge above the checkpoint'
        ],
        wildcards: [
          'A flock of messenger pigeons explodes from a rooftop loft and erases the view for several seconds',
          'A street preacher points at the chase and declares it an omen',
          'Two rival patrols arrive from opposite streets and immediately begin arguing over jurisdiction',
          'A pickpocket uses the confusion to steal something visible from a running character',
          'A public clock begins striking, masking every shouted warning',
          'A noble\'s exotic pet slips its leash and charges toward the commotion'
        ],
        sensory: ['hot stone, lamp oil, and shouted prices', 'canal water, bells, and iron wheels', 'steam, perfume, and wet tile', 'smoke, reflected light, and a thousand overlapping voices']
      },
      'urban-district': {
        landmarks: [
          'The alley passes beneath crowded tenements linked by laundry lines and wooden galleries',
          'The chase enters a workshop district where every doorway opens onto tools, sparks, and moving labor',
          'A row of narrow courtyards climbs the hill behind locked street gates',
          'The street runs beside a raised aqueduct supported by deep brick arches',
          'A maze of back stairs connects tavern kitchens, rented rooms, and roof terraces',
          'A drainage canal divides the district beneath a line of improvised footbridges'
        ],
        obstacles: [
          'A cooper rolls a tower of empty barrels into the alley and loses control of the entire stack',
          'A laundry pulley snaps overhead, dropping wet sheets across the passage like sudden walls',
          'Workers wheel a red-hot casting from one workshop to another on a narrow iron trolley',
          'A landlord slams the courtyard gate and begins locking it after mistaking the chase for a raid',
          'A chimney sweep falls through a roof hatch, sending soot, tiles, and tools across the upper path',
          'A line of handcarts backs up at the canal bridge while two drivers refuse to yield'
        ],
        opportunities: [
          'A tavern kitchen offers a rear cellar door, a dumbwaiter shaft, and a rack of hanging pans',
          'The aqueduct stair gives access to a narrow maintenance walk above the street',
          'A rope-and-pulley hoist is lifting timber toward a third-floor workshop',
          'An abandoned room opens onto a balcony close enough to jump to the next building',
          'A covered rubbish cart is rolling toward the district gate',
          'A cobbler\'s shop has floorboards lifted over an old service tunnel'
        ],
        wildcards: [
          'A dozen children begin racing alongside the party because they think this is a game',
          'Someone empties a washbasin from an upper window without looking down',
          'A chained guard dog breaks its post and drags the chain into the alley',
          'A tenant opens the wrong door and releases a cloud of startled chickens',
          'A workshop whistle sends every laborer into the street at once',
          'A rooftop argument ends with a chair thrown into the path below'
        ],
        sensory: ['coal smoke, wet laundry, and hammering metal', 'fried onions, soot, and close voices', 'brick dust, hot iron, and drainage water', 'rain on tiles and echoing footsteps']
      },
      dungeon: {
        landmarks: [
          'The corridor opens into a pillared crypt where every sarcophagus faces the same sealed door',
          'A narrow bridge crosses a vertical shaft filled with chains, counterweights, and darkness',
          'The chase enters an abandoned guard hall lined with arrow slits and murder holes',
          'A flooded gallery runs beneath cracked mosaics and statues with missing heads',
          'A spiral stair coils around a hollow column whose center drops beyond the torchlight',
          'The passage reaches a treasure chamber where dust lies untouched around one conspicuously clean chest'
        ],
        obstacles: [
          'A pressure plate clicks under the first footfall, and stone blades begin sweeping across the floor in a repeating pattern',
          'The clean chest unfolds into a tooth-lined maw and lashes at the nearest moving creature',
          'A portcullis drops halfway, jams crooked, and leaves a gap that is shrinking as the winch slips',
          'The mosaic floor breaks into rotating rings, carrying doors and statues out of alignment',
          'A cloud of pale cave moths pours from the ceiling and extinguishes every exposed flame',
          'A dead adventurer\'s pack catches on a tripwire, waking a corridor full of dart mechanisms'
        ],
        opportunities: [
          'An arrow slit is wide enough to reach the hidden guard passage if its stone facing can be removed',
          'A chandelier chain hangs over the shaft beside a counterweight heavy enough to launch or lift someone',
          'One sarcophagus lid has been shifted, revealing a crawlspace beneath the crypt floor',
          'A maintenance lever can reverse the rotating floor, but it stands on the far side of the chamber',
          'The flooded gallery contains a submerged bronze gate with air moving through it',
          'A cracked statue holds a stone key whose removal will almost certainly trigger something else'
        ],
        wildcards: [
          'Tiny scavengers begin stealing dropped equipment and vanishing through wall cracks',
          'A trapped spirit offers the correct route in exchange for a promise made immediately',
          'The dungeon\'s alarm gong starts sounding by itself, one chamber ahead at a time',
          'A rival delving party appears through a side door and assumes everyone else is the threat',
          'The walls begin whispering one character\'s name from different directions',
          'A wounded monster crawls from hiding and blocks the safest path without attacking yet'
        ],
        sensory: ['cold stone, old incense, and grinding gears', 'black water, rust, and distant impacts', 'dust, extinguished flame, and moving chains', 'stale air and the click of hidden mechanisms']
      },
      castle: {
        landmarks: [
          'The chase reaches a gatehouse chamber beneath a raised portcullis and murder holes',
          'A banquet hall stretches ahead beneath chandeliers, long tables, and a musicians\' gallery',
          'The battlements narrow around a tower roof exposed to wind and missile fire',
          'A chapel aisle leads toward stained glass, side vestries, and a locked sacristy door',
          'The kitchens form a furnace-hot maze of ovens, spits, servants, and hanging cookware',
          'A stable court opens around saddled horses, feed lofts, and two guarded exits'
        ],
        obstacles: [
          'The portcullis begins dropping while soldiers rush to jam the winch after it closes',
          'Servants overturn the banquet tables to form a barricade as armed retainers enter from the far doors',
          'A siege bell swings loose above the battlement stair, smashing stone and blocking the parapet in rhythm',
          'The chapel organ releases a thunderous chord as hidden shutters close across the exits',
          'A roasting spit snaps free and sends coals, grease, and iron hooks across the kitchen floor',
          'The stable master releases the horses to deny anyone a clean path through the court'
        ],
        opportunities: [
          'The winch chain can be cut, climbed, reversed, or used to pull the gate back upward',
          'The musicians\' gallery connects to a servants\' stair behind the wall hangings',
          'A rain gutter runs from the tower roof to a lower balcony outside the wall',
          'The sacristy contains a narrow burial stair descending beneath the chapel',
          'A flour lift rises from the kitchen cellar to a hatch near the great hall',
          'A half-saddled warhorse is tied beside an open postern gate'
        ],
        wildcards: [
          'A frightened page recognizes one of the characters and must decide whether to help',
          'A noble guest steps into the chase holding a ceremonial sword they clearly cannot use',
          'The castle hounds are released but stop to investigate a trail of kitchen meat',
          'A herald begins announcing the fugitives with an embarrassingly inaccurate description',
          'A servant opens a hidden panel, sees the chase, and tries to close it again',
          'A rack of tournament lances topples from the wall between both sides'
        ],
        sensory: ['hot grease, stone dust, and alarm bells', 'wax, incense, and armored footsteps', 'wind, rain, and iron chains', 'horse sweat, smoke, and shouted orders']
      },
      labyrinth: {
        landmarks: [
          'The passage divides around a circular chamber whose doors are numbered in an unknown script',
          'A corridor of mirrored bronze reflects turns that do not exist behind the party',
          'The chase reaches a garden maze grown around statues that slowly change direction',
          'A stair descends to the same landing from three different angles',
          'The walls open into a tiled hall marked with old blood, chalk arrows, and contradictory thread trails',
          'A central well gives a view of bridges crossing several levels of the maze'
        ],
        obstacles: [
          'Every door closes when another opens, forcing the group to choose who controls the sequence',
          'A section of wall rotates soundlessly and places the opposition\'s footsteps ahead instead of behind',
          'The statues begin moving whenever no one looks directly at them',
          'The stair changes direction after each landing and quietly separates anyone not holding contact',
          'Three thread trails lead onward, and all three carry signs that someone passed recently',
          'The bridges around the well retract one by one in a pattern that is almost understandable'
        ],
        opportunities: [
          'A maintenance groove at floor level reveals how the rotating wall is driven',
          'One bronze mirror can be angled to show around two impossible corners at once',
          'The statue bases contain sockets matching tools, weapons, or improvised wedges',
          'A stream of cool air rises from a crack beneath the false stair',
          'The old blood trail stops at a wall where the mortar is newer than the stone',
          'A dropped token from the opposition lies on a bridge they did not mean to reveal'
        ],
        wildcards: [
          'A voice identical to one character calls for help from the wrong corridor',
          'The maze repeats a sentence spoken earlier, but changes one important word',
          'A harmless-looking guide appears and refuses to say who it serves',
          'A bell rings whenever someone lies about knowing the way',
          'The opposition leaves a false trail that includes one genuine clue',
          'A door opens onto a room the party already crossed, now occupied by something new'
        ],
        sensory: ['cool air, distant echoes, and chalk dust', 'bronze reflections and footsteps without owners', 'wet leaves, stone, and bells heard through walls', 'stale air and the scrape of moving masonry']
      },
      caverns: {
        landmarks: [
          'The tunnel narrows onto a ledge above a black underground river',
          'A cathedral-sized chamber rises around columns of crystal and hanging stone',
          'The chase enters a fungal forest glowing in sickly bands of blue and green',
          'A natural chimney climbs through broken shelves of rock toward a slit of moving air',
          'The passage crosses a field of hollow stone tubes that resonate underfoot',
          'A mineral terrace descends toward steaming pools and thin crusts of salt'
        ],
        obstacles: [
          'A colony of pale bats erupts from the ceiling and turns sight, sound, and balance into chaos',
          'The river surges without warning, swallowing the lower half of the ledge',
          'Crystal needles begin shattering under the vibration of the pursuit',
          'The fungal caps release clouds of luminous spores when touched',
          'A slab of stone breaks loose and starts sliding down the chimney beneath the climbers',
          'The hollow tubes amplify every step until sections collapse like brittle pipes'
        ],
        opportunities: [
          'A rope bridge left by miners hangs folded against the opposite wall',
          'One crystal column can be toppled to form a bridge or block the tunnel',
          'The giant fungal caps are broad enough to bounce, hide beneath, or cut loose',
          'A cold draft reveals a narrow side fissure behind a curtain of mineral deposits',
          'An abandoned ore sled rests on rails descending through the tube field',
          'A chain of dry stone islands crosses the steaming pools beneath the mist'
        ],
        wildcards: [
          'Something large moves beneath the underground river without surfacing',
          'A blind cave creature mistakes the loudest runner for prey',
          'The crystals repeat spoken words several seconds later from the wrong direction',
          'A miner\'s warning bell begins ringing from a tunnel marked as collapsed',
          'A cloud of harmless insects reveals an invisible current of air',
          'The floor trembles with a rhythm unrelated to the pursuit'
        ],
        sensory: ['cold spray, mineral dust, and enormous echoes', 'wet stone and blue fungal light', 'sulfur, steam, and cracking crystal', 'moving air and distant water']
      },
      forest: {
        landmarks: [
          'The chase enters a stand of ancient trees whose roots rise higher than a person',
          'A ravine cuts across the path beneath a fallen pine and a curtain of vines',
          'The ground drops into an abandoned logging camp full of sleds, cables, and stacked timber',
          'A hunter\'s trail passes beneath rope snares and weathered observation platforms',
          'A flooded grove surrounds a ring of half-submerged standing stones',
          'The path climbs into the canopy where storm-felled trunks lean between living trees'
        ],
        obstacles: [
          'A herd of deer bursts across the path, followed by a panicked stag that refuses to turn',
          'The fallen pine begins rolling into the ravine as rotten roots tear free',
          'A timber stack collapses and sends stripped logs racing downhill',
          'A hidden snare catches the first loose pack and yanks it into the branches',
          'A swarm of angry insects boils from a split trunk after the pursuit strikes it',
          'A dead tree cracks overhead and begins falling across both possible paths'
        ],
        opportunities: [
          'A hunter\'s blind connects to a rope line stretched above the undergrowth',
          'The vines across the ravine are thick enough to swing from or braid into a crossing',
          'A logging sled sits on a steep track with its brake rope already frayed',
          'A hollow beneath the roots opens into a narrow animal tunnel',
          'The standing stones create dry footing and partial cover through the flooded grove',
          'A bent sapling is pinned under tension and could become a launcher, snare, or barrier'
        ],
        wildcards: [
          'Crows begin following the chase and loudly reveal every change of direction',
          'A territorial boar charges whichever side enters its wallow first',
          'A woodcutter appears ahead carrying an axe and no understanding of the danger',
          'The wind shifts and sends the scent of smoke from somewhere beyond the objective',
          'A fox darts from cover carrying a small object dropped earlier in the chase',
          'A tree marked as a safe trail bears fresh claw marks higher than anyone can reach'
        ],
        sensory: ['wet bark, crushed fern, and distant birds', 'pine resin, mud, and snapping branches', 'cold water and green shadow', 'woodsmoke and leaves shaken high above']
      },
      mountains: {
        landmarks: [
          'The trail narrows across a shale slope above a sheer drop',
          'A rope bridge spans a gorge between two abandoned watch posts',
          'The chase reaches a mountain shrine built beneath an overhanging shelf of ice',
          'A switchback climbs through a herd path crowded with stubborn goats',
          'An old ore cable crosses the valley toward a ruined loading station',
          'The ridge passes beneath a field of snow packed into dangerous cornices'
        ],
        obstacles: [
          'Loose stone begins sliding under every hurried step and gathers into a widening sheet',
          'One bridge anchor tears halfway out of the rock, dropping the crossing at a violent angle',
          'The shrine bell breaks free and rolls down the steps like a bronze boulder',
          'The goat herd panics and fills the switchback from wall to edge',
          'A cargo bucket on the ore cable starts moving without a rider as its brake fails',
          'A deep crack races across the snowfield and wakes the slope above'
        ],
        opportunities: [
          'A climber\'s fixed line runs above the shale on a harder but faster traverse',
          'The bridge ropes can be cut, tightened, swung from, or used to pull the span sideways',
          'The shrine prayer flags conceal iron pegs driven into the cliff face',
          'A shepherd\'s narrow tunnel passes behind the herd and emerges higher on the trail',
          'The ore bucket can carry two people, equipment, or a dangerous counterweight',
          'A rocky spine offers cover from the avalanche but no easy way back to the main path'
        ],
        wildcards: [
          'A mountain eagle dives at a bright object carried by one of the runners',
          'A lone pilgrim refuses to abandon the shrine bell rope',
          'The opposition appears briefly on a parallel ridge at the same height',
          'A horn call echoes from a watch post that looked deserted',
          'The weather closes in and erases the valley within seconds',
          'A pack animal breaks loose and drags a supply line across the trail'
        ],
        sensory: ['thin air, cold stone, and long echoes', 'snow glare and snapping rope', 'wind, iron, and goat bells', 'ice dust and the distant crack of the slope']
      },
      swamp: {
        landmarks: [
          'A rotting boardwalk crosses black water between walls of reed',
          'The chase reaches a drowned chapel whose roof and bell tower remain above the mire',
          'A patch of floating vegetation drifts across a channel wider than it first appeared',
          'The path threads through cypress roots and abandoned fishing platforms',
          'A field of pale gas bubbles surrounds the remains of a raised causeway',
          'The swamp opens around a half-sunken riverboat tangled in vines'
        ],
        obstacles: [
          'The boardwalk collapses plank by plank beneath the combined weight of the chase',
          'A cloud of biting insects descends so thickly that breathing and seeing become difficult',
          'The floating ground splits and begins drifting in opposite directions',
          'A submerged animal surges beneath the roots and overturns the nearest platform',
          'One gas pocket ignites from a lantern spark and sends blue flame racing across the water',
          'The riverboat shifts in the mud and its broken mast sweeps across the deck'
        ],
        opportunities: [
          'The chapel bell rope descends through a hole in the roof to a dry crypt below',
          'A narrow pirogue is tied beneath the boardwalk with one paddle still inside',
          'Long fishing poles can test the ground, vault gaps, or push the floating island',
          'A line of cypress knees forms a fast path for someone light enough to trust it',
          'The old causeway stones are visible beneath the gas bubbles in short broken sections',
          'The riverboat cargo hold opens onto a drainage channel behind the wreck'
        ],
        wildcards: [
          'A chorus of frogs stops all at once as something approaches through the reeds',
          'A lantern carried by a distant fisher begins moving toward the chase',
          'A cloud of fireflies gathers around one character and reveals them perfectly',
          'A huge heron attacks a familiar or small companion that passes beneath its nest',
          'The swamp repeats a shouted name from the wrong direction',
          'A corpse caught in the roots rises only because the current has changed'
        ],
        sensory: ['warm rot, insects, and black water', 'blue flame, wet wood, and choking mist', 'mud suction and reeds whispering together', 'stagnant water and distant splashes']
      },
      river: {
        landmarks: [
          'The chase reaches stone quays crowded with fishing boats, nets, and stacked crates',
          'A fast river cuts between the party and a ferry platform already pulling away',
          'The coastline narrows beneath cliffs riddled with tide caves',
          'A lock gate divides two levels of water beside a maintenance tower',
          'A floating market spreads across barges linked by loose gangplanks',
          'The route follows a seawall while storm waves break over the parapet'
        ],
        obstacles: [
          'A crane drops a net full of fish across the quay and sends silver bodies sliding underfoot',
          'The ferry cable snaps and lashes across the water as the platform swings downstream',
          'A wave fills the lower tide cave and cuts off the path back',
          'The lock keeper opens the wrong sluice, creating a violent current through the chamber',
          'Two barges drift apart while merchants and customers scramble between them',
          'A storm wave tears loose a row of mooring posts and sweeps ropes across the seawall'
        ],
        opportunities: [
          'A narrow skiff has a sail rigged but no crew aboard',
          'The remaining ferry cable can be climbed, reeved through a pulley, or cut at either bank',
          'A tide cave chimney opens above the flood line and smells of woodsmoke',
          'The maintenance tower controls ladders, sluices, and a bridge over the lock',
          'A fruit barge is moving toward the far bank with its cargo piled high enough to hide inside',
          'A lighthouse service stair climbs from the seawall to the cliff road'
        ],
        wildcards: [
          'A seal or river beast begins following the fastest-moving boat',
          'Dockworkers mistake the chase for a customs raid and start hiding contraband everywhere',
          'A child on the ferry throws a rope toward the party without permission',
          'A flock of gulls attacks a crate that breaks open in the middle of the path',
          'The harbor bell signals an incoming vessel that will block the channel',
          'A fisherman recognizes the target and loudly offers the wrong direction'
        ],
        sensory: ['salt, tar, and screaming gulls', 'cold spray and straining rope', 'fish scales, wet stone, and bells', 'storm wind and timber striking timber']
      },
      sewers: {
        landmarks: [
          'The brick channel divides beneath a row of rusted sluice gates',
          'A maintenance platform circles a deep settling tank beneath dripping arches',
          'The chase reaches an overflow chamber crowded with ladders, pipes, and inspection bridges',
          'A low culvert passes beneath the city wall with water nearly touching the ceiling',
          'An abandoned pumping station rises around flywheels and corroded pressure valves',
          'The sewer opens into a cistern where hundreds of small tunnels meet above the waterline'
        ],
        obstacles: [
          'A sluice gate slams open and sends a wall of foul water down the chosen channel',
          'A swarm of rats pours across the platform, fleeing something larger in the tank',
          'A pipe bursts and fills the chamber with scalding steam',
          'The culvert grate begins lowering as guards turn the mechanism from the street above',
          'A flywheel lurches back to life and drags loose chains through the pumping room',
          'Alchemical runoff changes color and begins dissolving the mortar around the nearest tunnel'
        ],
        opportunities: [
          'A maintenance cart sits on narrow rails descending toward the next junction',
          'The settling tank has a central axle that can be climbed or turned',
          'A ladder leads to a locked street hatch beneath a busy tavern cellar',
          'A dry inspection duct runs above the culvert for anyone able to reach it',
          'The pressure valves can flood, drain, or seal different parts of the station',
          'One tunnel carries fresh air and the sound of wheels from the district beyond'
        ],
        wildcards: [
          'A municipal worker rounds the corner holding a lantern and a clipboard',
          'The rats drag a dropped pouch toward a crack in the wall',
          'A voice from inside a pipe warns the party not to open the next gate',
          'A smuggler boat appears below the platform and demands payment for passage',
          'Something strikes the underside of the walkway hard enough to bend it',
          'The city bells above become audible through a grate, revealing exactly where the chase has reached'
        ],
        sensory: ['steam, rot, and iron echoes', 'foul water and grinding valves', 'chemical sharpness and wet brick', 'rats, dripping arches, and distant street noise']
      },
      ruins: {
        landmarks: [
          'The chase crosses a collapsed amphitheater overgrown with thorn and broken seating',
          'A cracked aqueduct strides across the valley on arches missing entire sections',
          'The path enters a roofless palace where faded mosaics cover unstable floors',
          'A fallen watchtower forms a tilted tunnel through its own shattered rooms',
          'A ruined temple stands around a sunken altar and columns bound with roots',
          'The remains of a market arcade create parallel lanes beneath leaning stone'
        ],
        obstacles: [
          'A section of seating gives way and sends a wave of stone down through the amphitheater',
          'The aqueduct channel floods from a broken cistern and turns the narrow top into a rushing stream',
          'A mosaic floor collapses into forgotten chambers below',
          'The tilted tower shifts and begins settling deeper into the rubble',
          'A nest of territorial creatures erupts from the altar hollow',
          'One arcade column breaks and starts a chain reaction through the roofless market'
        ],
        opportunities: [
          'Stage machinery beneath the amphitheater still connects trapdoors and lifting platforms',
          'A maintenance stair spirals inside the nearest aqueduct pier',
          'The exposed rooms below the mosaic offer a lower route through intact doorways',
          'A siege ladder trapped inside the tower can bridge the broken upper floor',
          'The temple roots form a climbable lattice around a sealed rear entrance',
          'An intact awning frame can be dropped to create cover, a ramp, or a barrier'
        ],
        wildcards: [
          'A faded fresco depicts the exact chase from an impossible angle',
          'Treasure hunters emerge carrying a heavy find they refuse to abandon',
          'A flock of nesting birds explodes from the ruins and reveals a hidden opening',
          'A stone head begins rolling down the slope after a minor impact',
          'The opposition appears through a breach thought to be impassable',
          'An old warning inscription becomes legible only as dust falls from it'
        ],
        sensory: ['hot dust, roots, and falling stone', 'old paint and hollow echoes', 'rainwater, weeds, and cracked masonry', 'bird wings and rubble shifting below']
      },
      planar: {
        landmarks: [
          'The chase enters a plain of black glass where reflections move a heartbeat too early',
          'A stairway climbs through open sky without touching any visible structure',
          'The path crosses a forest of doors standing alone in silver mist',
          'A city hangs upside down above the party, connected by streams flowing upward',
          'The ground becomes a mosaic of memories taken from everyone present',
          'A bridge of written words stretches across a void and changes under each footfall'
        ],
        obstacles: [
          'The reflections step out of the glass and begin blocking their originals',
          'Gravity turns sideways halfway up the stair',
          'Every door opens onto a place someone in the party fears losing',
          'The upward river breaks into floating spheres that carry sound but not air',
          'A memory beneath one character becomes solid and refuses to be crossed',
          'The written bridge erases words behind the group and removes the ideas they describe'
        ],
        opportunities: [
          'A reflection belonging to the opposition is moving in the wrong direction and can be followed',
          'Loose steps drift nearby and can be rearranged into a temporary platform',
          'One door has hinges on both sides and casts a shadow toward the objective',
          'A boat is falling upward through the river with its anchor trailing below',
          'A memory none of the characters recognizes forms a hidden path through the mosaic',
          'A sentence from the objective appears briefly among the bridge words'
        ],
        wildcards: [
          'One character hears their own voice giving instructions from the next beat',
          'The opposition loses its shadow, which continues the chase independently',
          'A harmless object becomes impossibly heavy when named aloud',
          'The timer, if present, briefly counts upward instead of down',
          'A familiar ally appears, but remembers events that never happened',
          'The landscape applauds a clever idea and punishes anyone who repeats it'
        ],
        sensory: ['ozone, glass, and voices heard backward', 'silver mist and weightless water', 'ink, cold light, and impossible distance', 'memories with physical texture']
      },
      jungle: {
        landmarks: [
          'The chase enters a strangler-fig grove where roots form walls, arches, and hidden hollows',
          'A rope bridge crosses a green ravine beneath a canopy crowded with vines and epiphytes',
          'The route reaches an overgrown stepped temple whose terraces vanish under roots and rainwater',
          'A flooded elephant trail cuts through bamboo taller than a house'
        ],
        obstacles: [
          'A troop of monkeys tears fruit, gear, and loose objects from anyone passing below',
          'A curtain of thorned vines drops after the first runner disturbs its supporting branch',
          'A sudden tropical downpour turns the temple stairs into waterfalls',
          'A territorial constrictor uncoils across the only dry branch above the flooded trail'
        ],
        opportunities: [
          'A hanging vine reaches across the ravine to a giant branch that bends toward the objective',
          'A hollow root tunnel passes beneath the densest vegetation',
          'The temple drainage channels form a steep but direct slide to the lower terrace',
          'A broad leaf canopy hides a hunter platform and a network of elevated walkways'
        ],
        wildcards: [
          'Bright birds repeat the loudest shouted words and reveal the chase in every direction',
          'A local guide appears on a high branch and offers help for an immediate promise',
          'A cloud of insects follows the warmest body in the group',
          'A ruined idol begins turning its stone head toward whoever carries magic'
        ],
        sensory: ['hot rain, crushed leaves, and animal calls', 'wet stone, flowers, and hidden water', 'green darkness and snapping bamboo', 'insects, mud, and thunder under the canopy']
      },
      road: {
        landmarks: [
          'The road runs between hedgerows toward a toll bridge and a line of waiting wagons',
          'A steep switchback climbs past a roadside shrine and an abandoned quarry',
          'The trade route crosses a coaching inn yard crowded with carts, animals, and travelers',
          'A milestone junction divides into a paved royal road and a narrow smugglers track'
        ],
        obstacles: [
          'A wagon loses a wheel and slews sideways across both lanes',
          'A herd of cattle spills through a broken fence and fills the road',
          'Tollkeepers begin dropping a timber barrier after hearing the chase approach',
          'A stone-hauling cart breaks its brake and rolls downhill toward everyone'
        ],
        opportunities: [
          'A mail coach is departing with fresh horses and an empty rear step',
          'The quarry tramline descends toward the lower road on a loaded flatbed',
          'A dry drainage culvert passes beneath the toll bridge',
          'The smugglers track cuts through a hedge too narrow for a full wagon'
        ],
        wildcards: [
          'A traveling peddler recognizes the opposition and deliberately points the wrong way',
          'A patrol rides into view from the next milestone',
          'A loose dog begins chasing the fastest wheel or hoof',
          'A funeral carriage refuses to yield the center of the road'
        ],
        sensory: ['dust, horse sweat, and hot iron', 'rain on packed earth and creaking wheels', 'hedges, dung, and distant bells', 'stone dust and leather tack']
      },
      plains: {
        landmarks: [
          'The chase crosses tall grass broken by a dry creek and scattered standing stones',
          'A herd trail runs toward a lone wind-bent tree on the horizon',
          'The ground rises into low burial mounds surrounded by open sky',
          'A shallow ravine cuts across the grassland beneath circling birds'
        ],
        obstacles: [
          'A stampeding herd crosses the line of pursuit in a wall of dust and horns',
          'Hidden burrows collapse beneath hooves, wheels, or running feet',
          'A grass fire races sideways on the wind and divides the open ground',
          'The dry creek becomes a sudden steep-sided trench at full speed'
        ],
        opportunities: [
          'The tall grass can conceal a sudden change of direction',
          'A standing stone offers height, cover, and a place to anchor rope or magic',
          'The ravine provides a lower route hidden from distant sight',
          'A riderless herd animal keeps pace close enough to catch or redirect'
        ],
        wildcards: [
          'A hunting bird dives at a bright or bloody object',
          'A distant camp begins signaling after spotting the chase',
          'A dust devil forms between both sides',
          'The ground ahead erupts with startled ground birds'
        ],
        sensory: ['dry grass, wind, and pounding earth', 'dust, open sky, and animal breath', 'sun-warmed stone and distant thunder', 'smoke, seed heads, and long sightlines']
      },
      desert: {
        landmarks: [
          'The chase reaches a canyon of red stone cut by narrow ledges and dry falls',
          'A caravan route winds between dunes toward a half-buried watchtower',
          'The ground becomes a salt flat broken by black rock and abandoned markers',
          'A ruined aqueduct crosses the badlands above a maze of gullies'
        ],
        obstacles: [
          'A dune face collapses and begins sliding beneath everyone on it',
          'A dust wall arrives from the side and erases the horizon',
          'A caravan of heavily loaded beasts blocks the only firm path',
          'The salt crust breaks over a pocket of caustic mud'
        ],
        opportunities: [
          'A wind-carved tunnel passes through the nearest ridge',
          'A discarded sand sail can be repaired or repurposed',
          'The aqueduct channel offers shade and a direct elevated line',
          'A line of old cistern vents marks a buried service route'
        ],
        wildcards: [
          'A mirage repeats the party at a different distance',
          'Scavengers begin circling low enough to reveal movement',
          'A caravan guard mistakes the chase for a raid',
          'A buried ruin exhales cold air through a newly opened crack'
        ],
        sensory: ['heat, salt, and windblown grit', 'dry stone and cloth snapping in the wind', 'sun glare and animal breath', 'cold shadow beneath burning air']
      },
      tundra: {
        landmarks: [
          'The chase crosses a frozen river beneath blue pressure ridges',
          'A line of standing stones rises from wind-scoured snow',
          'The route reaches a hunters camp built around sleds and hide shelters',
          'A glacier crevasse splits the white ground beneath a natural ice bridge'
        ],
        obstacles: [
          'The river ice begins cracking in a widening pattern',
          'A whiteout rolls across the route and erases every landmark',
          'A loaded sled breaks loose and accelerates down the slope',
          'A herd of shaggy animals crowds the narrow ice bridge'
        ],
        opportunities: [
          'A dog team is harnessed beside an unattended sled',
          'The pressure ridge forms a sheltered trench through the wind',
          'A meltwater tunnel runs beneath the glacier toward the far side',
          'The standing stones can anchor lines across the exposed ice'
        ],
        wildcards: [
          'A distant howl is answered much closer than expected',
          'Aurora light makes shadows point in impossible directions',
          'A hunter fires a warning arrow between both sides',
          'The ice begins carrying voices from far beneath the surface'
        ],
        sensory: ['knife-cold wind and snow glare', 'ice cracking under distant weight', 'fur, smoke, and frozen leather', 'blue light and breath turning to frost']
      },
      'open-sea': {
        landmarks: [
          'The chase runs between steep waves beneath a line of black storm clouds',
          'A chain of rocky islets breaks the horizon around narrow channels and hidden reefs',
          'The vessels approach a drifting field of wreckage and torn sailcloth',
          'A lighthouse island rises ahead while tide races around its foundations'
        ],
        obstacles: [
          'A wave breaks across the deck and tears loose lines, barrels, and one section of rail',
          'The wind shifts hard enough to back the sails and threaten the mast',
          'A submerged reef appears only when water drains from its jagged crest',
          'A damaged merchant vessel drifts broadside across the cleanest channel'
        ],
        opportunities: [
          'A following swell can carry a bold vessel over the slower water ahead',
          'The wreckage field provides cover, salvage, and dangerous shortcuts',
          'A narrow channel between islets favors the smaller or better-handled craft',
          'The lighthouse crane and mooring chains reach over the racing tide'
        ],
        wildcards: [
          'A sea creature surfaces between the vessels and reacts to vibration',
          'A signal flare rises from the drifting wreck',
          'A pod of large animals begins pacing the faster vessel',
          'Lightning reveals a third sail much closer than anyone believed'
        ],
        sensory: ['salt spray, wet rope, and straining timber', 'thunder, black water, and snapping canvas', 'tar, foam, and shouted orders', 'deep swells and iron fittings groaning']
      },
      sky: {
        landmarks: [
          'The chase threads between floating towers linked by chains and narrow bridges',
          'A cloud canyon opens around thunderheads lit from within',
          'The route crosses an aerial harbor crowded with airships, cranes, and signal kites',
          'A mountain-sized creature drifts through the sky beneath gardens built on its back'
        ],
        obstacles: [
          'A cargo airship turns across the route while its suspended containers swing wide',
          'A lightning discharge jumps between cloud banks and leaves charged air behind',
          'A flock of giant birds scatters into every altitude at once',
          'One floating tower begins rotating and drags its bridge chains through the airspace'
        ],
        opportunities: [
          'A thermal rises along the sunlit face of the nearest tower',
          'An airship wake offers speed and concealment beneath its hull',
          'A signal kite line can be caught, cut, climbed, or redirected',
          'The drifting creature enters a bank of cloud with sheltered air beneath its wings'
        ],
        wildcards: [
          'A courier on a winged mount mistakes one side for an escort',
          'A falling crate passes close enough to land on or use as cover',
          'The city warning beacons begin changing color',
          'A flock starts mimicking the formation of the chase'
        ],
        sensory: ['thin air, canvas, and distant thunder', 'sun glare and chains humming in wind', 'ozone, feathers, and cloud mist', 'engine vibration and open sky below']
      },
      underwater: {
        landmarks: [
          'The chase enters a coral canyon threaded with caves and columns of bubbles',
          'A drowned city street descends beneath arches covered in kelp and shell',
          'The route crosses a ship graveyard where masts point in every direction',
          'A volcanic trench glows beneath black water and rising mineral plumes'
        ],
        obstacles: [
          'A current tears through the canyon and fills the water with sand and shell',
          'A school of silver fish turns as one and becomes an opaque moving wall',
          'A wreck shifts on the slope and releases a tangle of ropes and broken spars',
          'A vent erupts with superheated water and forces the route sideways'
        ],
        opportunities: [
          'A rising bubble column can accelerate anyone willing to surrender precise control',
          'The drowned buildings connect through roof openings and collapsed upper rooms',
          'A buoyant cargo chest strains against a line leading through the wrecks',
          'A narrow coral tunnel blocks larger pursuers and hides an air pocket beyond'
        ],
        wildcards: [
          'A curious predator begins following the trail of disturbed water',
          'An aquatic settlement closes its shell gates ahead',
          'A ghostly bell rings from inside a sealed wreck',
          'Bioluminescent creatures attach themselves to the brightest magic'
        ],
        sensory: ['cold pressure, bubbles, and muted impacts', 'salt, silt, and filtered blue light', 'kelp movement and distant whale song', 'mineral heat beneath black water']
      },
      battlefield: {
        landmarks: [
          'The chase crosses trench lines between shattered siege engines and burning standards',
          'A ruined village lies inside the fighting, its streets divided by barricades and troop movements',
          'The route reaches a bridge contested by soldiers from both sides',
          'A field hospital operates beneath canvas while missiles pass overhead'
        ],
        obstacles: [
          'A cavalry unit charges across the route without seeing the chase inside the smoke',
          'A siege engine fires and collapses the ground where the party intended to cross',
          'Retreating soldiers flood the narrow street and carry everyone backward',
          'A supply wagon catches fire beside stacked ammunition'
        ],
        opportunities: [
          'A covered trench runs toward the objective beneath the line of fire',
          'An abandoned shield cart can provide moving cover',
          'A signal tower offers height, ropes, and control over nearby troop movements',
          'A wounded officer can command a gate opened if convinced quickly'
        ],
        wildcards: [
          'Both sides mistake the chase for a military maneuver',
          'A battlefield medic demands help with someone in immediate danger',
          'A war beast breaks its handlers and charges toward the loudest target',
          'A ceasefire horn sounds, but only part of the field obeys'
        ],
        sensory: ['smoke, mud, and iron impacts', 'burning canvas and shouted commands', 'blood, rain, and splintered timber', 'horns, hooves, and distant siege fire']
      },
      custom: {
        landmarks: [
          'The chase enters the most distinctive part of the environment described by the DM',
          'The immediate area narrows around a landmark that can be crossed, entered, climbed, or altered',
          'The ground ahead changes character and forces both sides to abandon the obvious line',
          'A boundary, structure, or natural feature divides the scene into competing routes',
          'The chase reaches a place where the environment is actively used by its inhabitants',
          'A visible landmark near the objective becomes the center of the next decision'
        ],
        obstacles: [
          'A moving element crosses the path and turns a straight sprint into a timing problem',
          'A fragile structure begins failing under the pressure of both sides',
          'Local inhabitants or creatures react to the chase and occupy the safest space',
          'A concealed hazard reveals itself only after the first character commits',
          'The opposition manipulates a feature of the environment to close the obvious route',
          'A sudden change in weather, machinery, magic, or terrain divides the group\'s options'
        ],
        opportunities: [
          'An open side space offers a route through, under, over, or behind the obstruction',
          'A vehicle, mechanism, animal, or movable object can be commandeered or redirected',
          'A high path offers speed at the price of exposure',
          'A concealed service route rewards careful observation',
          'A local tool or architectural feature can reshape the obstacle',
          'A risky shortcut points directly toward the objective'
        ],
        wildcards: [
          'A witness misunderstands the chase and intervenes',
          'A local creature reacts to movement, noise, or magic',
          'A third party appears with a separate goal',
          'Something dropped earlier returns in an unexpected way',
          'The environment reveals a hidden rule at the worst possible moment',
          'The safest-looking option contains a social or moral cost'
        ],
        sensory: ['the strongest sensory detail supplied by the environment', 'movement, noise, and a rapidly changing path', 'a landmark that makes the location specific', 'physical details the players can manipulate']
      }
    };

    const SCENE_PATTERNS = [
      {
        key: 'collision',
        type: 'Sudden collision',
        check: 'Turn the collision into forward momentum',
        instruction: 'Ask which visible element the party uses first: the obstruction, the side route, or the people and creatures caught in the middle.',
        approaches: [
          ['Dexterity (Acrobatics)', 'Pass through the moving gap before it closes.'],
          ['Strength (Athletics)', 'Redirect, overturn, or force the obstruction aside.'],
          ['Charisma or a clever command', 'Make the people inside the scene create the opening.']
        ]
      },
      {
        key: 'shortcut',
        type: 'Risky shortcut',
        check: 'Claim the shortcut without surrendering control',
        instruction: 'State the visible risk honestly, then ask what the party changes before committing.',
        approaches: [
          ['Wisdom (Perception)', 'Read the shortcut before trusting it.'],
          ['Dexterity or Strength', 'Take the dangerous line at speed.'],
          ['Tools, equipment, or magic', 'Stabilize or transform the shortcut.']
        ]
      },
      {
        key: 'living-obstacle',
        type: 'Living obstacle',
        check: 'Move through without letting panic decide the scene',
        instruction: 'Ask whether the party calms, redirects, avoids, recruits, or deliberately provokes the living element.',
        approaches: [
          ['Wisdom (Animal Handling or Insight)', 'Read and redirect the reaction.'],
          ['Charisma', 'Control the people caught in the chase.'],
          ['Stealth, illusion, or diversion', 'Move attention somewhere else.']
        ]
      },
      {
        key: 'threshold',
        type: 'Closing threshold',
        check: 'Beat, bypass, or reverse the closing route',
        instruction: 'Ask who commits to the mechanism or barrier while the others protect the group\'s movement.',
        approaches: [
          ['Strength (Athletics)', 'Hold or force the threshold.'],
          ['Dexterity (Acrobatics)', 'Pass through the remaining opening.'],
          ['Investigation, tools, or magic', 'Change how the mechanism works.']
        ]
      },
      {
        key: 'vertical',
        type: 'Vertical escape',
        check: 'Change levels without losing the party',
        instruction: 'Ask who goes first, who secures the route, and what is left exposed during the crossing.',
        approaches: [
          ['Strength (Athletics)', 'Climb, swing, pull, or anchor the group.'],
          ['Dexterity (Acrobatics)', 'Use the unstable upper line at speed.'],
          ['Rope, flight, ranged action, or magic', 'Create a route no one else expected.']
        ]
      },
      {
        key: 'hidden-danger',
        type: 'Hidden danger',
        check: 'Recognize the danger before it dictates the chase',
        instruction: 'Reveal enough detail for an informed choice. The hazard is pressure, not a secret answer the players must guess.',
        approaches: [
          ['Wisdom (Perception)', 'Notice the warning signs in time.'],
          ['Intelligence (Investigation)', 'Understand how the danger is triggered.'],
          ['Deliberate test or sacrificial resource', 'Expose the hazard without exposing a character.']
        ]
      },
      {
        key: 'false-safety',
        type: 'False refuge',
        check: 'Use the apparent refuge before it becomes a trap',
        instruction: 'Ask what the party searches, blocks, steals, opens, or abandons inside the temporary shelter.',
        approaches: [
          ['Dexterity (Stealth)', 'Break sight and leave by another exit.'],
          ['Intelligence (Investigation)', 'Find the real way through the space.'],
          ['Deception, disguise, or social action', 'Make the refuge conceal more than bodies.']
        ]
      },
      {
        key: 'opposition-play',
        type: 'Opposition maneuver',
        check: 'Counter the opposition\'s use of the environment',
        instruction: 'Show what the opposition is trying to achieve, then let the party answer the tactic rather than a predetermined skill.',
        approaches: [
          ['Wisdom (Insight or Perception)', 'Read the maneuver before it closes.'],
          ['Intelligence', 'Predict the line and arrive first.'],
          ['Deception, force, or control magic', 'Make the tactic work against its creator.']
        ]
      }
    ];

    const OBJECTIVE_THREADS = {
      opening: [
        'Everything now depends on one result: {objective}.',
        'Somewhere beyond the confusion lies the reason for running: {objective}.',
        'The pursuit matters only because the party still has to {objective}.'
      ],
      pressure: [
        'The next opening is valuable only if it still carries the party toward one result: {objective}.',
        'Every detour now competes with the need to {objective}.',
        'The objective remains possible, but the margin for reaching it is narrowing: {objective}.'
      ],
      final: [
        'Past this obstacle lies the final chance to {objective}.',
        'The objective is close enough to shape every choice now: {objective}.',
        'One decisive scene remains between the party and the attempt to {objective}.'
      ]
    };

    const CONTINUITY_MATRIX = {
      exceptional: [
        'The last maneuver did more than work; it forced {opposition} to recover before continuing.',
        '{character}\'s previous move still controls the pace, carrying the party into the next space with a clean lead.',
        'The advantage won moments ago remains visible, though the environment is already trying to take it back.'
      ],
      success: [
        'The opening created in the last beat carries the party forward before it can close.',
        'The previous obstacle is behind the party, and {opposition} has to choose a worse line around it.',
        'Momentum survives the last decision, but only just.'
      ],
      clever: [
        'The trick from the last beat buys several precious seconds before anyone understands what happened.',
        'The party\'s improvised solution is still confusing {opposition} when the next complication appears.',
        'A clever advantage carries forward, but it will not solve the new problem by itself.'
      ],
      cost: [
        'The price of the last success arrives with the party: {carry}.',
        'The party is still moving, but the last choice left a consequence in motion: {carry}.',
        'Nothing was lost for free in the previous beat; {carry}.'
      ],
      setback: [
        'The setback is immediate. {opposition} is close enough now to exploit the same space as the party.',
        'The last mistake erased the safest distance, and {opposition} enters the next scene almost on top of the party.',
        'There is no clean separation after the previous failure; the next obstacle belongs to both sides at once.'
      ],
      neutral: [
        'The last ruling leaves the balance unresolved, so the next feature of the environment becomes decisive.',
        'Neither side gained a clean advantage from the last exchange.',
        'The chase carries the previous decision forward without settling who controls the pace.'
      ]
    };

    const RESOLUTIONS = {
      exceptional: {
        title: 'Exceptional success',
        advantage: 2,
        peril: 0,
        text: 'The party gains clear momentum and creates an additional opportunity. Describe how the success changes the chase, not only how the obstacle is crossed.'
      },
      success: {
        title: 'Success',
        advantage: 1,
        peril: 0,
        text: 'The party clears the obstacle and keeps control of the chase. Carry one visible detail from this solution into the next beat.'
      },
      cost: {
        title: 'Success with a cost',
        advantage: 0,
        peril: 1,
        text: 'The party moves forward, but something becomes harder: position, secrecy, equipment, time, or safety. The cost becomes material for the next scene.'
      },
      setback: {
        title: 'Setback',
        advantage: -1,
        peril: 1,
        text: 'The chase turns against the party without stopping the story. Introduce separation, lost ground, exposure, a damaged resource, or a new complication.'
      },
      clever: {
        title: 'Clever solution',
        advantage: 1,
        peril: -1,
        text: 'The solution makes sense and changes the situation. No roll is needed. Reward the idea with momentum and describe the concrete advantage it creates.'
      },
      neutral: {
        title: 'DM ruling',
        advantage: 0,
        peril: 0,
        text: 'The DM resolves the action according to the fiction. Record the ruling and carry its consequences into the next beat.'
      }
    };

    document.querySelectorAll('[data-mcp-chase-tool]').forEach((root) => {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';

      const app = root.querySelector('[data-app]');
      const form = root.querySelector('[data-setup-form]');
      const characterList = root.querySelector('[data-character-list]');
      const characterError = root.querySelector('[data-character-error]');
      const spotlightSelect = form.elements.spotlightCharacter;
      const setupStatus = root.querySelector('[data-status-message]');
      const importInput = root.querySelector('[data-import-input]');
      const importChaseInput = root.querySelector('[data-import-chase-input]');
      const screens = root.querySelectorAll('[data-screen]');
      const toolGuide = root.querySelector('[data-tool-guide]');

      let state = createDefaultState();
      let timerInterval = null;
      let editingActiveChase = false;
      let timerWasRunningBeforeEdit = false;
      let pendingResetMode = null;
      let modalReturnFocus = null;
      const startButton = root.querySelector('[data-start-chase]');
      const confirmModal = root.querySelector('[data-confirm-modal]');

      function createDefaultState() {
        return {
          version: 6,
          setup: null,
          chase: {
            active: false,
            complete: false,
            beat: 1,
            viewBeat: 1,
            failureReason: null,
            totalBeats: 5,
            advantage: 0,
            peril: 0,
            currentScene: null,
            history: [],
            rejectedSceneKeys: [],
            timer: {
              enabled: false,
              durationSeconds: 0,
              endsAt: null,
              remainingSeconds: 0,
              paused: false
            }
          }
        };
      }

      function roleOptions(selectedValue = '') {
        return CHARACTER_ROLES.map(([value, label]) => {
          const selected = value === selectedValue ? ' selected' : '';
          return `<option value="${escapeHtml(value)}"${selected}>${escapeHtml(label)}</option>`;
        }).join('');
      }

      function weaknessOptions(selectedValue = '') {
        return CHARACTER_WEAKNESSES.map(([value, label]) => {
          const selected = value === selectedValue ? ' selected' : '';
          return `<option value="${escapeHtml(value)}"${selected}>${escapeHtml(label)}</option>`;
        }).join('');
      }

      function addCharacter(character = {}) {
        if (characterList.children.length >= 6) {
          showStatus('The tool supports a maximum of six characters.', 'setup');
          return;
        }

        const row = document.createElement('div');
        row.className = 'mcp-character-row';
        row.dataset.characterRow = '';
        row.innerHTML = `
          <div class="mcp-field">
            <label>Character name *</label>
            <input type="text" maxlength="60" data-character-name placeholder="Mara" value="${escapeHtml(character.name || '')}">
          </div>
          <div class="mcp-field">
            <label>Level</label>
            <input type="number" min="1" max="20" inputmode="numeric" data-character-level value="${escapeHtml(String(character.level || 1))}">
          </div>
          <div class="mcp-field">
            <label>Primary role</label>
            <select data-character-role-one>
              ${roleOptions(character.roleOne || 'agile')}
            </select>
          </div>
          <div class="mcp-field">
            <label>Secondary role</label>
            <select data-character-role-two>
              ${roleOptions(character.roleTwo || 'observant')}
            </select>
          </div>
          <div class="mcp-field">
            <label>Mechanical weakness</label>
            <select data-character-weakness>
              ${weaknessOptions(character.weakness || '')}
            </select>
          </div>
          <button class="mcp-remove-character" type="button" data-remove-character aria-label="Remove character" title="Remove character">×</button>
        `;

        characterList.appendChild(row);
        refreshSpotlightOptions();
      }

      function getCharacters() {
        return [...characterList.querySelectorAll('[data-character-row]')]
          .map((row) => ({
            name: row.querySelector('[data-character-name]').value.trim(),
            level: clampNumber(row.querySelector('[data-character-level]').value, 1, 20, 1),
            roleOne: row.querySelector('[data-character-role-one]').value,
            roleTwo: row.querySelector('[data-character-role-two]').value,
            weakness: row.querySelector('[data-character-weakness]').value
          }))
          .filter((character) => character.name);
      }

      function refreshSpotlightOptions(selectedName = spotlightSelect.value) {
        const characters = getCharacters();
        spotlightSelect.innerHTML = '<option value="">No preference</option>';
        characters.forEach((character) => {
          const option = document.createElement('option');
          option.value = character.name;
          option.textContent = character.name;
          if (character.name === selectedName) option.selected = true;
          spotlightSelect.appendChild(option);
        });
      }

      function serializeSetup() {
        const formData = new FormData(form);
        return {
          sessionTitle: String(formData.get('sessionTitle') || '').trim(),
          partyRole: String(formData.get('partyRole') || 'escaping'),
          characters: getCharacters(),
          oppositionName: String(formData.get('oppositionName') || '').trim(),
          oppositionScale: String(formData.get('oppositionScale') || 'few'),
          oppositionType: String(formData.get('oppositionType') || 'humanoids'),
          oppositionPower: String(formData.get('oppositionPower') || 'comparable'),
          oppositionBehavior: String(formData.get('oppositionBehavior') || 'clever'),
          chaseMode: String(formData.get('chaseMode') || 'foot'),
          environment: String(formData.get('environment') || 'dungeon'),
          customEnvironment: String(formData.get('customEnvironment') || '').trim(),
          environmentScale: String(formData.get('environmentScale') || 'contained'),
          environmentTraits: formData.getAll('environmentTraits').map(String),
          difficulty: String(formData.get('difficulty') || 'standard'),
          consequences: String(formData.get('consequences') || 'serious'),
          tone: String(formData.get('tone') || 'cinematic'),
          length: clampNumber(formData.get('length'), 3, 9, 5),
          objective: String(formData.get('objective') || 'escape'),
          objectiveDetail: String(formData.get('objectiveDetail') || '').trim(),
          useTimer: formData.get('useTimer') === 'on',
          timerMinutes: clampNumber(formData.get('timerMinutes'), 1, 180, 10),
          startingAdvantage: clampNumber(formData.get('startingAdvantage'), -2, 2, 0),
          visibility: String(formData.get('visibility') || 'clear'),
          uncertainty: String(formData.get('uncertainty') || 'medium'),
          spotlightCharacter: String(formData.get('spotlightCharacter') || '')
        };
      }

      function validateSetup(setup) {
        clearInvalidFields();
        let valid = true;

        if (!setup.characters.length) {
          characterError.hidden = false;
          valid = false;
        } else {
          characterError.hidden = true;
        }

        if (!setup.oppositionName) {
          markInvalid(form.elements.oppositionName);
          valid = false;
        }

        if (setup.environment === 'custom' && !setup.customEnvironment) {
          markInvalid(form.elements.customEnvironment);
          valid = false;
        }

        if (!valid) {
          showStatus('Complete the highlighted fields before starting the chase.', 'setup');
          const firstInvalid = root.querySelector('[aria-invalid="true"], [data-character-error]:not([hidden])');
          if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return valid;
      }

      function markInvalid(field) {
        if (!field) return;
        field.setAttribute('aria-invalid', 'true');
      }

      function clearInvalidFields() {
        root.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
      }

      function populateForm(setup) {
        if (!setup) return;

        form.elements.sessionTitle.value = setup.sessionTitle || '';
        const partyRole = form.querySelector(`input[name="partyRole"][value="${cssEscape(setup.partyRole || 'escaping')}"]`);
        if (partyRole) partyRole.checked = true;
        form.elements.oppositionName.value = setup.oppositionName || '';
        setSelect(form.elements.oppositionScale, setup.oppositionScale);
        setSelect(form.elements.oppositionType, setup.oppositionType);
        setSelect(form.elements.oppositionPower, setup.oppositionPower);
        setSelect(form.elements.oppositionBehavior, setup.oppositionBehavior);
        setSelect(form.elements.chaseMode, setup.chaseMode || 'foot');
        setSelect(form.elements.environment, setup.environment);
        form.elements.customEnvironment.value = setup.customEnvironment || '';
        setSelect(form.elements.environmentScale, setup.environmentScale);
        setSelect(form.elements.difficulty, setup.difficulty);
        setSelect(form.elements.consequences, setup.consequences);
        setSelect(form.elements.tone, setup.tone);
        setSelect(form.elements.namedItem('length'), String(setup.length || 5));
        setSelect(form.elements.objective, setup.objective);
        form.elements.objectiveDetail.value = setup.objectiveDetail || '';
        form.elements.useTimer.checked = Boolean(setup.useTimer);
        form.elements.timerMinutes.value = setup.timerMinutes || 10;
        setSelect(form.elements.startingAdvantage, String(setup.startingAdvantage ?? 0));
        setSelect(form.elements.visibility, setup.visibility);
        setSelect(form.elements.uncertainty, setup.uncertainty);

        const savedTraits = Array.isArray(setup.environmentTraits)
          ? setup.environmentTraits.map((trait) => trait === 'magical' ? 'magically-warped' : trait)
          : [];
        form.querySelectorAll('input[name="environmentTraits"]').forEach((input) => {
          input.checked = savedTraits.includes(input.value);
        });

        characterList.innerHTML = '';
        const characters = Array.isArray(setup.characters) && setup.characters.length ? setup.characters : [{}];
        characters.slice(0, 6).forEach(addCharacter);
        refreshSpotlightOptions(setup.spotlightCharacter || '');
        updateConditionalFields();
        updateOppositionLanguage();
      }

      function setSelect(select, value) {
        if (!select || value === undefined || value === null) return;
        const option = [...select.options].find((item) => item.value === String(value));
        if (option) select.value = String(value);
      }

      function updateConditionalFields() {
        const timerEnabled = form.elements.useTimer.checked;
        root.querySelector('[data-timer-field]').hidden = !timerEnabled;
        form.elements.timerMinutes.required = timerEnabled;

        const customEnvironment = form.elements.environment.value === 'custom';
        root.querySelector('[data-custom-environment-field]').hidden = !customEnvironment;
        form.elements.customEnvironment.required = customEnvironment;
      }

      function updateOppositionLanguage() {
        const role = form.querySelector('input[name="partyRole"]:checked')?.value || 'escaping';
        const kicker = root.querySelector('[data-opposition-kicker]');
        const heading = root.querySelector('[data-opposition-heading]');
        if (role === 'pursuing') {
          kicker.textContent = 'The target';
          heading.textContent = 'Who is the party chasing?';
          form.elements.oppositionName.placeholder = 'Veyra, the royal assassin';
        } else {
          kicker.textContent = 'The pursuers';
          heading.textContent = 'Who is chasing the party?';
          form.elements.oppositionName.placeholder = 'A bristling horde of goblins';
        }
      }

      function startChase(setup) {
        state = createDefaultState();
        state.setup = setup;
        state.chase.active = true;
        state.chase.totalBeats = setup.length;
        state.chase.advantage = setup.startingAdvantage;
        state.chase.peril = 0;
        state.chase.viewBeat = 1;

        if (setup.useTimer) {
          state.chase.timer.enabled = true;
          state.chase.timer.durationSeconds = setup.timerMinutes * 60;
          state.chase.timer.remainingSeconds = setup.timerMinutes * 60;
          state.chase.timer.endsAt = Date.now() + setup.timerMinutes * 60 * 1000;
          state.chase.timer.paused = false;
        }

        state.chase.currentScene = buildScene();
        renderLiveScreen();
        showScreen('live');
        startTimerLoop();
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      function buildScene() {
        const setup = state.setup;
        const environmentKey = ENVIRONMENT_KITS[setup.environment] ? setup.environment : 'custom';
        const kit = ENVIRONMENT_KITS[environmentKey] || ENVIRONMENT_KITS.custom;
        const pattern = chooseScenePattern(environmentKey);
        const character = chooseSpotlightCharacter();
        const trait = chooseTrait();
        const stage = chaseStage();
        const modeKey = CHASE_MODE_MATRIX[setup.chaseMode] ? setup.chaseMode : 'foot';
        const modeProfile = CHASE_MODE_MATRIX[modeKey];
        const seedBase = hashString(`${setup.sessionTitle}|${environmentKey}|${modeKey}|${state.chase.beat}|${state.chase.peril}|${state.chase.advantage}`);

        const landmark = pickEnvironmentAsset(kit, 'landmarks', seedBase + 1, environmentKey);
        const obstacle = pickEnvironmentAsset(kit, 'obstacles', seedBase + 7, environmentKey);
        const opportunity = pickEnvironmentAsset(kit, 'opportunities', seedBase + 13, environmentKey);
        const wildcard = pickEnvironmentAsset(kit, 'wildcards', seedBase + 19, environmentKey);
        const sensory = pickEnvironmentAsset(kit, 'sensory', seedBase + 23, environmentKey);
        const modeMovement = pickModeAsset(modeProfile, 'movement', seedBase + 29, modeKey);
        const modeOpportunity = pickModeAsset(modeProfile, 'opportunities', seedBase + 31, modeKey);
        const modeRisk = pickModeAsset(modeProfile, 'risks', seedBase + 37, modeKey);
        const assetKeys = [landmark.key, obstacle.key, opportunity.key, wildcard.key, sensory.key, modeMovement.key, modeOpportunity.key, modeRisk.key];

        const scene = {
          key: `${environmentKey}:${pattern.key}:${assetKeys.join('|')}`,
          patternKey: pattern.key,
          type: pattern.type,
          character: character.name,
          trait,
          stage,
          weakness: character.weakness || '',
          assetKeys,
          landmark: landmark.value,
          obstacle: obstacle.value,
          opportunity: opportunity.value,
          wildcard: wildcard.value,
          sensory: sensory.value,
          modeKey,
          modeMovement: modeMovement.value,
          modeOpportunity: modeOpportunity.value,
          modeRisk: modeRisk.value,
          check: pattern.check,
          baseInstruction: pattern.instruction,
          baseApproaches: deepClone(pattern.approaches),
          affordances: buildAffordances(opportunity.value, obstacle.value, wildcard.value, modeOpportunity.value),
          baseNote: buildSceneBaseNote(pattern, opportunity.value, obstacle.value, wildcard.value, modeProfile)
        };

        scene.coreSentences = buildEnvironmentalCore(scene, setup, environmentKey);
        scene.weaknessRelevant = weaknessApplies(character, scene, trait);
        scene.successText = buildSceneSuccessText(scene, character);
        scene.costText = buildSceneCostText(scene, character, trait);
        scene.setbackText = buildSceneSetbackText(scene, character, trait);
        scene.carryForward = {
          success: buildCarryForward(scene, 'success'),
          cost: buildCarryForward(scene, 'cost'),
          setback: buildCarryForward(scene, 'setback')
        };
        scene.prompt = buildReadAloud(scene, character, trait, stage);
        scene.note = buildDmNote(scene, character, trait, stage);
        scene.instruction = buildInstruction(scene, character);
        scene.approaches = buildApproaches(scene, character);
        scene.dc = calculateDC(scene);
        return scene;
      }

      function chooseScenePattern(environmentKey) {
        const used = new Set([
          ...state.chase.history.map((entry) => entry.scene?.patternKey).filter(Boolean),
          ...(state.chase.rejectedSceneKeys || []).map((key) => String(key).split(':')[1]).filter(Boolean)
        ]);
        let candidates = SCENE_PATTERNS.filter((pattern) => !used.has(pattern.key));
        if (!candidates.length) candidates = SCENE_PATTERNS;
        const uncertainty = state.setup.uncertainty === 'high' ? 5 : state.setup.uncertainty === 'low' ? 1 : 3;
        return candidates[Math.abs(state.chase.beat * uncertainty + state.chase.peril + state.chase.advantage) % candidates.length];
      }

      function pickEnvironmentAsset(kit, group, seed, environmentKey) {
        const primary = Array.isArray(kit[group]) && kit[group].length ? kit[group] : ENVIRONMENT_KITS.custom[group];
        const fallback = environmentKey === 'custom'
          ? []
          : (ENVIRONMENT_KITS.custom[group] || []).map((value, index) => adaptFallbackAsset(value, group, environmentKey, index));
        const used = new Set(state.chase.history.flatMap((entry) => entry.scene?.assetKeys || []));
        const rejected = new Set(state.chase.rejectedSceneKeys || []);

        const chooseFrom = (values, offsetBase = 0) => {
          if (!values.length) return null;
          const start = Math.abs(seed) % values.length;
          for (let offset = 0; offset < values.length; offset += 1) {
            const localIndex = (start + offset) % values.length;
            const index = offsetBase + localIndex;
            const key = `${environmentKey}:${group}:${index}`;
            if (!used.has(key) && ![...rejected].some((sceneKey) => sceneKey.includes(key))) {
              return { value: values[localIndex], key };
            }
          }
          return null;
        };

        return chooseFrom(primary, 0)
          || chooseFrom(fallback, primary.length)
          || { value: primary[Math.abs(seed) % primary.length], key: `${environmentKey}:${group}:repeat-${Math.abs(seed) % primary.length}` };
      }

      function pickModeAsset(profile, group, seed, modeKey) {
        const values = Array.isArray(profile?.[group]) && profile[group].length ? profile[group] : ['The method of travel becomes part of the immediate problem.'];
        const used = new Set(state.chase.history.flatMap((entry) => entry.scene?.assetKeys || []));
        const start = Math.abs(seed) % values.length;
        for (let offset = 0; offset < values.length; offset += 1) {
          const index = (start + offset) % values.length;
          const key = `mode-${modeKey}:${group}:${index}`;
          if (!used.has(key)) return { value: values[index], key };
        }
        const index = start;
        return { value: values[index], key: `mode-${modeKey}:${group}:repeat-${index}` };
      }

      function adaptFallbackAsset(value, group, environmentKey, index) {
        const environment = ENVIRONMENT_LABELS[environmentKey] || environmentKey;
        const text = stripEnding(value);
        if (group === 'landmarks') return `Deeper in the ${environment}, ${lowerFirst(text)}`;
        if (group === 'sensory') return `${text}, unmistakably shaped by the ${environment}`;
        const prefixes = ['Nearby,', 'At the next turn,', 'Before either side can settle,', 'At the edge of the scene,', 'A few strides ahead,', 'Without warning,'];
        return `${prefixes[index % prefixes.length]} ${lowerFirst(text)}`;
      }

      function hashString(value) {
        let hash = 0;
        const text = String(value || '');
        for (let index = 0; index < text.length; index += 1) {
          hash = ((hash << 5) - hash + text.charCodeAt(index)) | 0;
        }
        return Math.abs(hash);
      }

      function buildEnvironmentalCore(scene, setup, environmentKey) {
        const location = environmentKey === 'custom' && setup.customEnvironment
          ? `The chase enters ${stripEnding(lowerFirst(setup.customEnvironment))}`
          : scene.landmark;
        const sensoryLine = scene.sensory ? `The air is full of ${stripEnding(lowerFirst(scene.sensory))}.` : '';
        const modeLine = scene.modeMovement ? `${stripEnding(capitalizeSentence(scene.modeMovement))}.` : '';
        const modeRisk = scene.modeRisk ? `${stripEnding(capitalizeSentence(scene.modeRisk))}.` : '';
        const obstacle = capitalizeSentence(scene.obstacle);
        const opportunity = capitalizeSentence(scene.opportunity);
        const wildcard = capitalizeSentence(scene.wildcard);

        if (scene.patternKey === 'shortcut') {
          return [
            `${stripEnding(location)}.`,
            modeLine,
            `${stripEnding(opportunity)}.`,
            `${stripEnding(obstacle)}.`,
            `${stripEnding(wildcard)}.`
          ];
        }
        if (scene.patternKey === 'living-obstacle') {
          return [
            `${stripEnding(location)}.`,
            modeLine,
            `${stripEnding(wildcard)}.`,
            `${stripEnding(obstacle)}.`,
            `${stripEnding(opportunity)}.`
          ];
        }
        if (scene.patternKey === 'false-safety') {
          return [
            `${stripEnding(location)}.`,
            modeLine,
            `${stripEnding(opportunity)}.`,
            'For half a second, it looks like shelter rather than another trap.',
            `${stripEnding(wildcard)}.`,
            `${stripEnding(obstacle)}.`
          ];
        }
        if (scene.patternKey === 'opposition-play') {
          return [
            `${stripEnding(location)}.`,
            modeLine,
            `${stripEnding(obstacle)}.`,
            `${capitalizeSentence(state.setup.oppositionName)} immediately tries to turn that change against you.`,
            `${stripEnding(opportunity)}.`
          ];
        }
        if (scene.patternKey === 'hidden-danger') {
          return [
            `${stripEnding(location)}.`,
            modeLine,
            sensoryLine,
            `${stripEnding(opportunity)}.`,
            `${stripEnding(obstacle)}.`,
            'The fastest line is also the one that exposes the hidden danger.',
            `${stripEnding(wildcard)}.`
          ];
        }
        if (scene.patternKey === 'vertical') {
          return [
            `${stripEnding(location)}.`,
            modeLine,
            `${stripEnding(obstacle)}.`,
            `${stripEnding(opportunity)}.`,
            'The useful feature creates a second level to the chase.',
            `${stripEnding(wildcard)}.`
          ];
        }
        if (scene.patternKey === 'threshold') {
          return [
            `${stripEnding(location)}.`,
            modeLine,
            `${stripEnding(obstacle)}.`,
            `${stripEnding(opportunity)}.`,
            'That feature is the only visible way to beat or reverse the closure.',
            sensoryLine
          ];
        }
        return [
          `${stripEnding(location)}.`,
            modeLine,
          sensoryLine,
          `${stripEnding(obstacle)}.`,
          modeRisk,
          `${stripEnding(opportunity)}; ${lowerFirst(stripEnding(wildcard))}.`
        ];
      }

      function buildAffordances(opportunity, obstacle, wildcard, modeOpportunity) {
        return [
          shortenAffordance(opportunity),
          shortenAffordance(modeOpportunity),
          shortenAffordance(obstacle),
          shortenAffordance(wildcard)
        ].filter(Boolean);
      }

      function shortenAffordance(value) {
        const text = stripEnding(value);
        if (!text) return '';
        return text.length > 145 ? `${text.slice(0, 142).trim()}...` : text;
      }

      function buildSceneBaseNote(pattern, opportunity, obstacle, wildcard, modeProfile) {
        return `${pattern.instruction} Keep the travel method mechanically active. ${modeProfile?.dm || ''} Concrete elements available for improvisation: ${stripEnding(lowerFirst(opportunity))}; ${stripEnding(lowerFirst(modeProfile?.opportunities?.[0] || 'the travel method itself'))}; ${stripEnding(lowerFirst(obstacle))}; ${stripEnding(lowerFirst(wildcard))}.`;
      }

      function buildSceneSuccessText(scene, character) {
        const variants = [
          `${character.name} makes the useful feature of the scene work before the danger can close around it. ${capitalizeSentence(stripEnding(scene.opportunity))} becomes the party's way through.`,
          `${character.name} reads both the environment and the method of travel correctly. ${capitalizeSentence(stripEnding(scene.modeOpportunity || scene.opportunity))} creates the lead the party needed.`,
          `The plan turns movement itself into leverage. ${capitalizeSentence(stripEnding(scene.modeOpportunity || scene.opportunity))} carries the party clear of the immediate threat.`,
          `${character.name} commits at the right instant, and the same hazard that threatened the party now delays the opposition instead.`
        ];
        return pickStable(variants, hashString(scene.key));
      }

      function buildSceneCostText(scene, character, trait) {
        const traitCost = TRAIT_MATRIX[trait]?.cost;
        if (traitCost) return traitCost;
        const modeCost = CHASE_MODE_MATRIX[scene.modeKey]?.cost;
        if (modeCost && scene.modeKey !== 'foot') return modeCost;
        const variants = [
          `${character.name} gets everyone through, but ${lowerFirst(stripEnding(scene.wildcard))} becomes a problem that follows into the next space.`,
          `The opening works, though the party leaves evidence, equipment, or a vulnerable position behind near ${lowerFirst(stripEnding(scene.landmark))}.`,
          `The shortcut holds just long enough. In exchange, ${lowerFirst(stripEnding(scene.obstacle))} changes the ground behind and ahead.`
        ];
        return pickStable(variants, hashString(`${scene.key}:cost`));
      }

      function buildSceneSetbackText(scene, character, trait) {
        const traitSetback = TRAIT_MATRIX[trait]?.setback;
        if (traitSetback) return traitSetback;
        const modeSetback = CHASE_MODE_MATRIX[scene.modeKey]?.setback;
        if (modeSetback && scene.modeKey !== 'foot') return modeSetback;
        const opposition = state.setup.oppositionName || 'the opposition';
        const variants = [
          `${capitalizeSentence(opposition)} reaches the same obstacle before the party has cleared it, turning the space into a shared and immediate danger.`,
          `The attempt misreads the timing. ${capitalizeSentence(stripEnding(scene.obstacle))}, and the opposition gains the cleaner line around it.`,
          `${character.name} is forced off the intended approach. ${capitalizeSentence(stripEnding(scene.wildcard))}, leaving the next decision under direct pressure.`
        ];
        return pickStable(variants, hashString(`${scene.key}:setback`));
      }

      function buildCarryForward(scene, kind) {
        if (kind === 'success') return `${lowerFirst(stripEnding(scene.modeOpportunity || scene.opportunity))} remains under the party's control`;
        if (kind === 'cost') return `${lowerFirst(stripEnding(scene.modeRisk || scene.wildcard))} is still affecting the chase`;
        return `${lowerFirst(stripEnding(scene.modeRisk || scene.obstacle))} has given the opposition a closer line`;
      }

      function chooseSpotlightCharacter() {
        const characters = state.setup.characters;
        const preferred = characters.find((character) => character.name === state.setup.spotlightCharacter);
        if (preferred && !state.chase.history.some((entry) => entry.character === preferred.name)) return preferred;

        const spotlightCounts = Object.fromEntries(characters.map((character) => [character.name, 0]));
        state.chase.history.forEach((entry) => {
          if (Object.hasOwn(spotlightCounts, entry.character)) spotlightCounts[entry.character] += 1;
        });
        return [...characters].sort((a, b) => spotlightCounts[a.name] - spotlightCounts[b.name])[0] || characters[0];
      }

      function chooseTrait() {
        const traits = Array.isArray(state.setup.environmentTraits) ? state.setup.environmentTraits : [];
        if (!traits.length) return '';
        const recent = state.chase.history.slice(-Math.min(3, traits.length)).map((entry) => entry.scene?.trait).filter(Boolean);
        const candidates = traits.filter((trait) => !recent.includes(trait));
        const pool = candidates.length ? candidates : traits;
        return pool[Math.abs(state.chase.beat + state.chase.peril) % pool.length];
      }

      function chaseStage() {
        const ratio = state.chase.beat / Math.max(1, state.chase.totalBeats);
        if (state.chase.beat === 1) return 'opening';
        if (ratio >= 0.78) return 'final';
        return 'pressure';
      }

      function buildReadAloud(scene, character, trait, stage) {
        const setup = state.setup;
        const sentences = [];
        const continuity = buildContinuitySentence();
        const tone = TONE_PROFILES[setup.tone] || TONE_PROFILES.cinematic;

        if (continuity) {
          sentences.push(continuity);
        } else {
          sentences.push(pickFreshVariant(tone.opening, hashString(scene.key)) || pickStable(tone.opening, hashString(scene.key)));
          if (['enormous', 'labyrinthine'].includes(setup.environmentScale)) sentences.push(freshSentence(SCALE_NARRATION[setup.environmentScale] || ''));
          if (setup.visibility !== 'clear') sentences.push(freshSentence(VISIBILITY_NARRATION[setup.visibility] || ''));
          if (Number(setup.startingAdvantage) !== 0) sentences.push(freshSentence(STARTING_POSITION_NARRATION[String(setup.startingAdvantage)] || ''));
        }

        sentences.push(...scene.coreSentences);

        const traitLine = TRAIT_MATRIX[trait]?.read || '';
        if (traitLine && !scene.coreSentences.some((line) => sentenceSimilarity(line, traitLine) > 0.55)) {
          sentences.push(freshSentence(traitLine));
        }

        if (scene.patternKey !== 'opposition-play') sentences.push(buildOppositionBeatLine(scene));
        sentences.push(buildSpotlightLine(character, trait, scene));
        sentences.push(buildPressureLine());
        sentences.push(buildObjectiveThread(stage));

        const narrative = polishNarrative(sentences.map((line) => toSecondPerson(replaceTokens(line, character))));
        return varyOppositionMentions(narrative);
      }

      function buildEnvironmentOpening() {
        const setup = state.setup;
        if (setup.environment === 'custom') return `The chase enters ${stripEnding(lowerFirst(setup.customEnvironment || 'the environment described by the DM'))}.`;
        const environment = ENVIRONMENT_LABELS[setup.environment] || setup.environment;
        const variants = [
          `The chase breaks into the ${environment}.`,
          `The ${environment} becomes part of the pursuit immediately.`,
          `Both sides are forced to read the ${environment} at speed.`
        ];
        return pickStable(variants, hashString(`${setup.sessionTitle}:${environment}`));
      }

      function buildContinuitySentence() {
        const previous = state.chase.history[state.chase.history.length - 1];
        if (!previous) return '';
        const key = previous.outcomeKey || 'neutral';
        const variants = CONTINUITY_MATRIX[key] || CONTINUITY_MATRIX.neutral;
        const carryKind = key === 'cost' ? 'cost' : key === 'setback' ? 'setback' : 'success';
        const carry = previous.scene?.carryForward?.[carryKind]
          || previous.scene?.costText
          || previous.scene?.setbackText
          || 'the consequences of the last decision are still in motion';
        const template = pickFreshVariant(variants, hashString(`${previous.sceneKey}:${state.chase.beat}`));
        const opposition = state.setup.oppositionName || 'the opposition';
        const character = previous.character || 'the lead character';

        if (template) {
          return capitalizeSentence(String(template)
            .replaceAll('{opposition}', opposition)
            .replaceAll('{character}', character)
            .replaceAll('{carry}', lowerFirst(stripEnding(carry))));
        }

        const landmark = lowerFirst(stripEnding(previous.scene?.landmark || previous.scene?.type || 'the last obstacle'));
        const dynamic = {
          exceptional: `${character}'s solution at ${landmark} still dictates the pace, forcing ${opposition} to react before pressing on.`,
          success: `The line won at ${landmark} carries forward, but the next part of the environment is already closing around it.`,
          clever: `The trick used at ${landmark} is still buying time when a different danger interrupts the escape.`,
          cost: `The group leaves ${landmark} behind, but ${lowerFirst(stripEnding(carry))} follows into the next space.`,
          setback: `The failed attempt at ${landmark} erased the safe gap; ${opposition} now reaches the next obstacle close enough to interfere directly.`,
          neutral: `What happened at ${landmark} remains unresolved, so the next physical feature decides who controls the pace.`
        };
        return freshSentence(dynamic[key] || dynamic.neutral) || capitalizeSentence(dynamic[key] || dynamic.neutral);
      }

      function buildOppositionBeatLine(scene) {
        const setup = state.setup;
        const previous = state.chase.history[state.chase.history.length - 1];
        let line = '';
        if (previous?.outcomeKey === 'setback') {
          line = capitalizeSentence(replaceTokens(BEHAVIOR_MATRIX[setup.oppositionBehavior]?.[setup.partyRole] || '', null));
        } else {
          const cycle = state.chase.beat % 3;
          if (cycle === 1) line = OPPOSITION_SCALE_MATRIX[setup.oppositionScale]?.[setup.partyRole] || '';
          else if (cycle === 2) line = capitalizeSentence(replaceTokens(BEHAVIOR_MATRIX[setup.oppositionBehavior]?.[setup.partyRole] || '', null));
          else line = OPPOSITION_TYPE_MATRIX[setup.oppositionType]?.[setup.partyRole] || '';
        }

        const fresh = freshSentence(line);
        if (fresh) return fresh;

        const opposition = capitalizeSentence(state.setup.oppositionName || 'the opposition');
        const landmark = capitalizeSentence(stripEnding(scene?.landmark || 'The next part of the environment changes shape'));
        const opportunity = capitalizeSentence(stripEnding(scene?.opportunity || 'A single useful opening remains visible'));
        const alternatives = state.setup.partyRole === 'escaping'
          ? [
              `${opposition} stops matching your path and commits to the far side of the scene, trying to meet you where the space narrows.`,
              `${opposition} abandons the obvious line and races for the same advantage. ${opportunity}.`,
              `${opposition} changes tactics and secures the side of the scene you have not yet protected. The new line runs through the landmark ahead: ${landmark}.`
            ]
          : [
              `${opposition} recognizes the danger and changes direction before you can close the trap.`,
              `${opposition} uses the only useful opening to conceal its next movement. ${opportunity}.`,
              `${opposition} abandons the obvious line and forces you to read the environment again. Its new path bends around the landmark ahead: ${landmark}.`
            ];
        return pickFreshVariant(alternatives, hashString(`${scene?.key}:opposition`));
      }

      function weaknessApplies(character, scene, trait = '') {
        const profile = WEAKNESS_MATRIX[character?.weakness];
        if (!profile) return false;
        if (profile.traits?.includes(trait)) return true;
        const searchable = [
          scene?.type,
          scene?.check,
          ...(scene?.coreSentences || []),
          scene?.baseInstruction,
          ...(Array.isArray(scene?.baseApproaches) ? scene.baseApproaches.flat() : [])
        ].join(' ').toLowerCase();
        return profile.keywords.some((keyword) => searchable.includes(keyword));
      }

      function weaknessProfile(character) {
        return WEAKNESS_MATRIX[character?.weakness] || null;
      }

      function buildSpotlightLine(character, trait, scene) {
        const primary = ROLE_MATRIX[character.roleOne] || ROLE_MATRIX.observant;
        const roleVariants = [
          replaceTokens(primary.read, character),
          `${character.name} is the first to see how their ${capitalizeWords(character.roleOne)} instincts could turn one visible detail into an advantage.`,
          `The scene gives ${character.name} a clear chance to lead through ${lowerFirst(primary.check)} rather than raw speed alone.`,
          `Among the details ahead, ${character.name} recognizes the one that rewards ${capitalizeWords(character.roleOne)} thinking.`,
          `This obstacle favors a decision ${character.name} can make through ${lowerFirst(primary.check)}, before anyone else commits to the obvious line.`,
          `${character.name} sees how to turn this ${lowerFirst(scene?.type || 'challenge')} into a way forward instead of merely enduring it.`
        ];
        let roleLine = pickFreshVariant(roleVariants, hashString(`${scene.key}:${character.name}`));
        if (!roleLine) {
          roleLine = `${character.name} finds a practical use for something everyone else treated as scenery, turning the ${lowerFirst(scene?.type || 'challenge')} against the pursuit.`;
        }
        const profile = weaknessProfile(character);
        if (!profile || !scene?.weaknessRelevant) return roleLine;
        const weaknessLine = freshSentence(replaceTokens(profile.read, character));
        return weaknessLine ? `${roleLine} ${weaknessLine}` : roleLine;
      }

      function buildObjectiveThread(stage) {
        const beat = state.chase.beat;
        const total = state.chase.totalBeats;
        const midpoint = Math.ceil(total / 2);
        if (beat !== 1 && beat !== midpoint && stage !== 'final') return '';
        const objective = lowerFirst(stripEnding(objectiveText()));
        const templates = OBJECTIVE_THREADS[stage] || OBJECTIVE_THREADS.pressure;
        const selected = pickFreshVariant(templates, hashString(`${objective}:${beat}`));
        if (selected) return selected.replaceAll('{objective}', objective);
        return freshSentence(`What happens here changes whether you can still ${objective}.`);
      }

      function buildPressureLine() {
        const setup = state.setup;
        const previous = state.chase.history[state.chase.history.length - 1];
        let line = '';
        if (state.chase.timer.enabled && state.chase.timer.remainingSeconds <= Math.max(60, state.chase.timer.durationSeconds * 0.35)) {
          line = `The countdown is now visible in every choice: ${formatTime(state.chase.timer.remainingSeconds)} remains.`;
        } else if (previous?.outcomeKey === 'cost' || previous?.outcomeKey === 'setback') {
          line = '';
        } else if (state.chase.peril >= 3) {
          line = 'One more severe complication could transform the chase into capture, separation, or an immediate confrontation.';
        } else if (state.chase.advantage >= 2) {
          line = setup.partyRole === 'escaping' ? 'You have a meaningful lead, and this obstacle is the chance to make it decisive.' : 'You are close enough that the target is changing its behavior in response to you.';
        } else if (state.chase.advantage <= -2) {
          line = setup.partyRole === 'escaping' ? 'The pursuit is close enough to affect the same objects and people you touch.' : 'The target is beginning to disappear into the environment rather than merely run through it.';
        }
        return freshSentence(line);
      }

      function buildDmNote(scene, character, trait, stage) {
        const setup = state.setup;
        const traitNote = TRAIT_MATRIX[trait]?.dm || '';
        const modeNote = CHASE_MODE_MATRIX[setup.chaseMode || 'foot']?.dm || '';
        const consequenceNote = CONSEQUENCE_MATRIX[setup.consequences]?.pressure || CONSEQUENCE_MATRIX.serious.pressure;
        const weaknessNote = scene.weaknessRelevant ? weaknessProfile(character)?.dm || '' : '';
        const continuityNote = state.chase.history.length
          ? 'Begin from the previous outcome. Do not reset distance, damage, witnesses, lost equipment, or route changes between beats.'
          : 'Establish the starting distance and make the objective visible before asking for a roll.';
        const stageNote = stage === 'final'
          ? 'This beat can end the chase. Tie the final choice directly to the stated objective.'
          : 'Let the obstacle change position, resources, information, or access rather than acting as a detached skill check.';
        return polishNarrative([
          scene.baseNote,
          continuityNote,
          stageNote,
          traitNote,
          modeNote,
          weaknessNote,
          consequenceNote,
          `The party is tier ${partyTier()}; spells, class features, equipment, and sacrifices appropriate to that tier should genuinely change the scene.`
        ]);
      }

      function buildInstruction(scene, character) {
        const roleOne = ROLE_MATRIX[character.roleOne] || ROLE_MATRIX.observant;
        const roleTwo = ROLE_MATRIX[character.roleTwo] || ROLE_MATRIX.support;
        const profile = weaknessProfile(character);
        const weakness = scene.weaknessRelevant && profile
          ? ` The listed DC includes the relevant ${profile.dc > 1 ? `+${profile.dc}` : '+1'} weakness pressure, but a plan that avoids that weakness should remove the modifier.`
          : '';
        const mode = CHASE_MODE_MATRIX[scene.modeKey || state.setup.chaseMode || 'foot'];
        return `${scene.baseInstruction} Begin with ${character.name}. Their ${capitalizeWords(character.roleOne)} and ${capitalizeWords(character.roleTwo)} strengths suggest ${roleOne.check} or ${roleTwo.check}. The ${mode?.label || 'travel method'} option is ${mode?.check?.[0] || 'a relevant movement check'}: ${mode?.check?.[1] || 'use the method of travel directly'}. Accept any approach that uses a visible element in the scene.${weakness}`;
      }

      function buildApproaches(scene, character) {
        const roleOne = ROLE_MATRIX[character.roleOne] || ROLE_MATRIX.observant;
        const roleTwo = ROLE_MATRIX[character.roleTwo] || ROLE_MATRIX.support;
        const mode = CHASE_MODE_MATRIX[scene.modeKey || state.setup.chaseMode || 'foot'];
        const approaches = [
          [mode?.check?.[0] || 'Relevant movement check', mode?.check?.[1] || 'Use the travel method directly.'],
          [roleOne.check, `${character.name}: ${roleOne.approach}`],
          [roleTwo.check, `${character.name}: ${roleTwo.approach}`]
        ];
        scene.baseApproaches.forEach((approach) => {
          if (approaches.length < 4 && !approaches.some(([title]) => title === approach[0])) approaches.push(approach);
        });
        approaches.push(['No roll', `A spell, feature, item, sacrifice, or decisive use of ${lowerFirst(scene.affordances[0] || 'the environment')} can change the fiction without a check.`]);
        return approaches.slice(0, 5);
      }

      function calculateDC(scene) {
        let dc = DIFFICULTY_DC[state.setup.difficulty] || 13;
        if (state.setup.oppositionPower === 'superior') dc += 1;
        if (state.setup.oppositionPower === 'overwhelming') dc += 2;
        if (state.setup.visibility === 'poor') dc += 1;
        if (state.setup.visibility === 'almost-none') dc += 2;
        if (state.setup.uncertainty === 'high' && ['hidden-danger', 'false-safety', 'opposition-play'].includes(scene.patternKey)) dc += 1;
        if (['flying', 'underwater', 'magical-transit'].includes(state.setup.chaseMode) && state.setup.difficulty !== 'forgiving') dc += 1;
        if (state.setup.chaseMode === 'naval' && (state.setup.environmentTraits || []).includes('rough-seas')) dc += 1;
        if (state.setup.chaseMode === 'mounted' && (state.setup.environmentTraits || []).includes('frightened-mounts')) dc += 1;
        if (state.setup.chaseMode === 'vehicle' && (state.setup.environmentTraits || []).includes('loose-cargo')) dc += 1;
        if (state.chase.peril >= 3) dc += 1;
        if (state.chase.advantage >= 2) dc -= 1;
        if (state.chase.advantage <= -2) dc += 1;

        const previous = state.chase.history[state.chase.history.length - 1];
        if (previous?.outcomeKey === 'setback') dc += 1;
        if (previous?.outcomeKey === 'exceptional' || previous?.outcomeKey === 'clever') dc -= 1;

        if (scene.weaknessRelevant) {
          const character = state.setup.characters.find((item) => item.name === scene.character);
          const profile = weaknessProfile(character);
          if (profile) dc += profile.dc;
        }
        return clampNumber(dc, 5, 30, 13);
      }

      function polishNarrative(values) {
        const sentences = cleanSentences(values)
          .flatMap((value) => splitIntoSentences(value))
          .map((value) => capitalizeSentence(value.trim()))
          .filter(Boolean);
        const accepted = [];
        sentences.forEach((sentence) => {
          if (!accepted.some((existing) => sentenceSimilarity(existing, sentence) >= 0.66)) accepted.push(sentence);
        });
        return varyRepeatedTerms(accepted.join(' '));
      }

      function splitIntoSentences(value) {
        const text = String(value || '').replace(/\s+/g, ' ').trim();
        if (!text) return [];
        return text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
      }

      function sentenceSimilarity(a, b) {
        const stop = new Set(['the', 'a', 'an', 'and', 'or', 'to', 'of', 'in', 'on', 'at', 'for', 'with', 'is', 'are', 'was', 'were', 'it', 'this', 'that', 'you', 'your', 'party']);
        const words = (value) => new Set(String(value || '').toLowerCase().match(/[a-z0-9']+/g)?.filter((word) => word.length > 2 && !stop.has(word)) || []);
        const left = words(a);
        const right = words(b);
        if (!left.size || !right.size) return 0;
        const intersection = [...left].filter((word) => right.has(word)).length;
        const union = new Set([...left, ...right]).size;
        return union ? intersection / union : 0;
      }

      function varyRepeatedTerms(value) {
        const replacements = {
          route: ['way', 'path', 'ground'],
          opening: ['gap', 'chance', 'way through'],
          distance: ['space', 'lead', 'separation'],
          moment: ['instant', 'heartbeat', 'second'],
          pressure: ['danger', 'urgency', 'threat'],
          matter: ['count', 'shape the outcome', 'carry weight'],
          matters: ['counts', 'shapes what follows', 'carries weight'],
          obstacle: ['hazard', 'barrier', 'problem'],
          choice: ['decision', 'move', 'commitment']
        };
        let output = String(value || '');
        Object.entries(replacements).forEach(([word, alternatives]) => {
          let count = 0;
          output = output.replace(new RegExp(`\\b${word}\\b`, 'gi'), (match) => {
            count += 1;
            if (count <= 1) return match;
            const replacement = alternatives[(count - 2) % alternatives.length];
            return match.charAt(0) === match.charAt(0).toUpperCase() ? capitalizeSentence(replacement) : replacement;
          });
        });
        return output.replace(/\s+/g, ' ').trim();
      }


      function varyOppositionMentions(value) {
        const name = String(state.setup.oppositionName || '').trim();
        const text = String(value || '');
        if (!name || !text) return text;
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(escaped, 'gi');
        let count = 0;
        const aliases = state.setup.partyRole === 'escaping' ? ['the pursuing force', 'the opposition'] : ['the quarry', 'the target'];
        return text.replace(pattern, (match, offset, fullText) => {
          count += 1;
          if (count === 1) return match;
          const alias = aliases[(count - 2) % aliases.length];
          const before = fullText.slice(0, offset).trimEnd();
          const sentenceStart = !before || /[.!?]$/.test(before);
          return sentenceStart ? capitalizeSentence(alias) : alias;
        });
      }

      function chooseConsequenceText(trait, kind) {
        const setup = state.setup;
        const traitText = TRAIT_MATRIX[trait]?.[kind];
        if (traitText) return traitText;
        const profile = CONSEQUENCE_MATRIX[setup.consequences] || CONSEQUENCE_MATRIX.serious;
        return kind === 'cost' ? profile.genericCost : profile.genericSetback;
      }

      function objectiveText() {
        const setup = state.setup;
        return setup.objectiveDetail || OBJECTIVE_LABELS[setup.objective] || setup.objective || 'complete the objective';
      }

      function partyTier() {
        const average = state.setup.characters.reduce((sum, character) => sum + Number(character.level || 1), 0) / Math.max(1, state.setup.characters.length);
        if (average >= 17) return 4;
        if (average >= 11) return 3;
        if (average >= 5) return 2;
        return 1;
      }

      function replaceTokens(text, character) {
        return String(text || '')
          .replaceAll('{character}', character?.name || 'one character')
          .replaceAll('{opposition}', state.setup.oppositionName || 'the opposition');
      }

      function toSecondPerson(text) {
        return String(text || '')
          .replaceAll('The party still has', 'You still have')
          .replaceAll('the party still has', 'you still have')
          .replaceAll('Behind the party', 'Behind you')
          .replaceAll('behind the party', 'behind you')
          .replaceAll('The party has', 'You have')
          .replaceAll('the party has', 'you have')
          .replaceAll('The party is', 'You are')
          .replaceAll('the party is', 'you are')
          .replaceAll('The party', 'You')
          .replaceAll('the party', 'you')
          .replaceAll('you still has', 'you still have')
          .replaceAll('You still has', 'You still have')
          .replaceAll('you has', 'you have')
          .replaceAll('You has', 'You have')
          .replaceAll('you is', 'you are')
          .replaceAll('You is', 'You are');
      }

      function pickStable(values, seed = 0) {
        if (!Array.isArray(values) || !values.length) return '';
        return values[Math.abs(Number(seed) || 0) % values.length];
      }

      function normalizeNarrativeSentence(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/\{[^}]+\}/g, '')
          .replace(/[^a-z0-9']+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }

      function historyNarrativeSentenceList() {
        const used = [];
        state.chase.history.forEach((entry) => {
          splitIntoSentences(entry?.prompt || '').forEach((sentence) => {
            const text = String(sentence || '').trim();
            if (text) used.push(text);
          });
        });
        return used;
      }

      function isNarrativeSentenceUsed(sentence, used = historyNarrativeSentenceList()) {
        const normalized = normalizeNarrativeSentence(sentence);
        if (!normalized) return true;
        return used.some((existing) => {
          const existingNormalized = normalizeNarrativeSentence(existing);
          return existingNormalized === normalized || sentenceSimilarity(existing, sentence) >= 0.72;
        });
      }

      function freshSentence(value) {
        const text = String(value || '').trim();
        if (!text) return '';
        const used = historyNarrativeSentenceList();
        const candidateSentences = splitIntoSentences(text);
        return candidateSentences.some((sentence) => isNarrativeSentenceUsed(sentence, used)) ? '' : text;
      }

      function pickFreshVariant(values, seed = 0) {
        if (!Array.isArray(values) || !values.length) return '';
        const used = historyNarrativeSentenceList();
        const start = Math.abs(Number(seed) || 0) % values.length;
        for (let offset = 0; offset < values.length; offset += 1) {
          const candidate = String(values[(start + offset) % values.length] || '').trim();
          const candidateSentences = splitIntoSentences(candidate);
          if (candidate && candidateSentences.length && !candidateSentences.some((sentence) => isNarrativeSentenceUsed(sentence, used))) {
            return candidate;
          }
        }
        return '';
      }

      function cleanSentences(values) {
        return values.map((value) => String(value || '').trim()).filter(Boolean);
      }

      function stripEnding(value) {
        return String(value || '').trim().replace(/[.!?]+$/, '');
      }

      function lowerFirst(value) {
        const text = String(value || '').trim();
        return text ? text.charAt(0).toLowerCase() + text.slice(1) : '';
      }

      function capitalizeSentence(value) {
        const text = String(value || '').trim();
        return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
      }

      function getHistoryEntry(beat) {
        return state.chase.history.find((entry) => Number(entry.beat) === Number(beat)) || null;
      }

      function sceneFromHistory(entry) {
        if (entry?.scene) return entry.scene;
        return {
          key: entry?.sceneKey || `history-${entry?.beat || 0}`,
          type: entry?.sceneType || 'Resolved challenge',
          character: entry?.character || state.setup.characters[0]?.name || '',
          prompt: entry?.prompt || '',
          note: 'This beat has already been resolved. Review it without changing the current chase state.',
          check: entry?.check || 'Resolved check',
          instruction: 'This is a previous beat. Use Next to return to the active challenge.',
          approaches: [],
          affordances: entry?.scene?.affordances || [],
          dc: entry?.dc || 13
        };
      }

      function renderLiveScreen() {
        const setup = state.setup;
        const displayBeat = clampNumber(state.chase.viewBeat || state.chase.beat, 1, state.chase.beat, state.chase.beat);
        state.chase.viewBeat = displayBeat;
        const historyEntry = getHistoryEntry(displayBeat);
        const isCurrentBeat = displayBeat === state.chase.beat;
        const scene = historyEntry ? sceneFromHistory(historyEntry) : state.chase.currentScene;
        const resolved = Boolean(historyEntry);

        root.querySelector('[data-live-title]').textContent = setup.sessionTitle || 'Untitled chase';
        root.querySelector('[data-beat-status]').textContent = `${displayBeat} / ${state.chase.totalBeats}`;
        root.querySelector('[data-view-status]').textContent = `Beat ${displayBeat} of ${state.chase.totalBeats}${resolved ? ' · resolved' : ' · active'}`;
        root.querySelector('[data-advantage-status]').textContent = signedNumber(state.chase.advantage);
        root.querySelector('[data-peril-status]').textContent = `${state.chase.peril} / 4`;
        root.querySelector('[data-scene-type]').textContent = scene.type;
        root.querySelector('[data-narrative-prompt]').textContent = scene.prompt;
        root.querySelector('[data-dm-note]').textContent = scene.note;
        root.querySelector('[data-check-name]').textContent = scene.check;
        root.querySelector('[data-check-instruction]').textContent = scene.instruction;
        root.querySelector('[data-dc-input]').value = historyEntry?.dc || scene.dc;
        root.querySelector('[data-dc-input]').disabled = resolved;
        root.querySelector('[data-roll-total]').value = '';
        root.querySelector('[data-natural-roll]').value = '';
        root.querySelector('[data-result-entry]').hidden = resolved;
        root.querySelector('.mcp-manual-results').hidden = resolved;
        root.querySelector('[data-resolution]').hidden = !resolved;

        const affordances = root.querySelector('[data-scene-affordances]');
        affordances.innerHTML = (scene.affordances || []).map((item) => `<li>${escapeHtml(item)}</li>`).join('');
        root.querySelector('[data-affordances-panel]').hidden = !(scene.affordances || []).length;

        const regenerate = root.querySelector('[data-reroll-scene]');
        regenerate.disabled = resolved || !isCurrentBeat;
        regenerate.setAttribute('aria-disabled', regenerate.disabled ? 'true' : 'false');

        if (resolved) {
          root.querySelector('[data-resolution-title]').textContent = historyEntry.outcomeTitle;
          root.querySelector('[data-resolution-text]').textContent = historyEntry.outcomeText;
        }

        const approaches = root.querySelector('[data-approaches]');
        approaches.innerHTML = '';
        scene.approaches.forEach(([title, description]) => {
          const item = document.createElement('div');
          item.className = 'mcp-approach';
          item.innerHTML = `<strong>${escapeHtml(title)}</strong><span>${escapeHtml(description)}</span>`;
          approaches.appendChild(item);
        });

        const previousButton = root.querySelector('[data-prev-scene]');
        const nextButton = root.querySelector('[data-next-scene]');
        previousButton.disabled = false;
        previousButton.textContent = displayBeat <= 1 ? 'Back to setup' : 'Previous';
        nextButton.disabled = isCurrentBeat ? !resolved : false;
        nextButton.textContent = isCurrentBeat && resolved ? (displayBeat >= state.chase.totalBeats ? 'Finish chase' : 'Next beat') : 'Next';

        const resolutionBack = root.querySelector('[data-resolution-back]');
        const resolutionNext = root.querySelector('[data-resolution-next]');
        resolutionBack.textContent = displayBeat <= 1 ? 'Back to setup' : 'Previous beat';
        resolutionNext.textContent = isCurrentBeat
          ? (displayBeat >= state.chase.totalBeats ? 'Finish chase' : 'Continue to next beat')
          : 'Return toward current beat';
        resolutionNext.disabled = !resolved;

        renderLiveSummary();
        renderHistory();
        renderTimer();
      }


      function renderLiveSummary() {
        const setup = state.setup;
        const environment = setup.environment === 'custom'
          ? setup.customEnvironment
          : ENVIRONMENT_LABELS[setup.environment] || setup.environment;
        const objective = setup.objectiveDetail || OBJECTIVE_LABELS[setup.objective] || setup.objective;
        const entries = [
          ['Party', setup.partyRole === 'escaping' ? 'Escaping' : 'Pursuing'],
          ['Characters', setup.characters.map((character) => `${character.name} (L${character.level})`).join(', ')],
          ['Opposition', setup.oppositionName],
          ['Mode', CHASE_MODE_LABELS[setup.chaseMode || 'foot'] || setup.chaseMode],
          ['Route', environment],
          ['Tone', capitalizeWords(setup.tone)],
          ['Objective', objective]
        ];

        root.querySelector('[data-live-summary]').innerHTML = entries.map(([term, value]) => (
          `<div><dt>${escapeHtml(term)}</dt><dd>${escapeHtml(value)}</dd></div>`
        )).join('');
      }

      function renderHistory() {
        const list = root.querySelector('[data-history-list]');
        if (!state.chase.history.length) {
          list.innerHTML = '<li class="mcp-history__empty">No resolved beats yet.</li>';
          return;
        }

        list.innerHTML = state.chase.history.map((entry) => (
          `<li><strong>Beat ${entry.beat}: ${escapeHtml(entry.outcomeTitle)}</strong><br>${escapeHtml(entry.check)}${entry.rollTotal !== null ? `, total ${entry.rollTotal} vs DC ${entry.dc}` : ''}.</li>`
        )).join('');
      }

      function resolveRoll() {
        const rollInput = root.querySelector('[data-roll-total]');
        const total = Number(rollInput.value);
        if (!Number.isFinite(total) || rollInput.value === '') {
          rollInput.setAttribute('aria-invalid', 'true');
          rollInput.focus();
          return;
        }
        rollInput.removeAttribute('aria-invalid');

        const dc = clampNumber(root.querySelector('[data-dc-input]').value, 5, 30, state.chase.currentScene.dc);
        const natural = root.querySelector('[data-natural-roll]').value;
        let resultKey;

        if (total >= dc + 5) resultKey = 'exceptional';
        else if (total >= dc) resultKey = 'success';
        else if (total >= dc - 4) resultKey = 'cost';
        else resultKey = 'setback';

        let extra = '';
        if (natural === '20') extra = ' The natural 20 can add a vivid benefit if that suits the table.';
        if (natural === '1') extra = ' The natural 1 can add a vivid complication if that suits the table.';

        applyResolution(resultKey, {
          rollTotal: total,
          dc,
          extra
        });
      }

      function applyResolution(resultKey, details = {}) {
        const resolution = RESOLUTIONS[resultKey] || RESOLUTIONS.neutral;
        const oldAdvantage = state.chase.advantage;
        const oldPeril = state.chase.peril;

        state.chase.advantage = clampNumber(state.chase.advantage + resolution.advantage, -3, 3, 0);
        state.chase.peril = clampNumber(state.chase.peril + resolution.peril, 0, 4, 0);

        const outcomeText = buildOutcomeNarration(resultKey, state.chase.currentScene, details.extra || '');
        const historyEntry = {
          beat: state.chase.beat,
          sceneKey: state.chase.currentScene.key,
          sceneType: state.chase.currentScene.type,
          character: state.chase.currentScene.character,
          prompt: state.chase.currentScene.prompt,
          check: state.chase.currentScene.check,
          outcomeKey: resultKey,
          outcomeTitle: resolution.title,
          outcomeText,
          rollTotal: Number.isFinite(details.rollTotal) ? details.rollTotal : null,
          dc: Number.isFinite(details.dc) ? details.dc : state.chase.currentScene.dc,
          advantageChange: state.chase.advantage - oldAdvantage,
          perilChange: state.chase.peril - oldPeril,
          timestamp: new Date().toISOString(),
          scene: deepClone(state.chase.currentScene)
        };

        state.chase.history.push(historyEntry);
        state.chase.viewBeat = state.chase.beat;
        renderLiveScreen();
      }

      function buildOutcomeNarration(resultKey, scene, extra = '') {
        const setup = state.setup;
        const character = setup.characters.find((item) => item.name === scene.character) || setup.characters[0];
        const opposition = setup.oppositionName || 'the opposition';
        const objective = lowerFirst(stripEnding(objectiveText()));
        const finalBeat = state.chase.beat >= state.chase.totalBeats;

        if (resultKey === 'exceptional') {
          return polishNarrative([
            scene.successText,
            setup.partyRole === 'escaping'
              ? `${capitalizeSentence(opposition)} is forced to stop, split, or take a slower line around the changed environment.`
              : `${capitalizeSentence(opposition)} loses the freedom to choose the next path and must react to the party.`,
            finalBeat ? `The advantage opens a decisive chance to ${objective}.` : 'The next beat begins with the party controlling both pace and position.',
            extra
          ]);
        }

        if (resultKey === 'success' || resultKey === 'clever') {
          const methodLine = resultKey === 'clever'
            ? 'The plan succeeds because it uses the fiction already present in the scene; no die is needed to make a sound idea real.'
            : 'The timing holds, and the environment changes in the party\'s favor.';
          return polishNarrative([
            scene.successText,
            methodLine,
            finalBeat ? `The way toward the final attempt to ${objective} is open.` : 'The party reaches the next space before the other side can recover.',
            extra
          ]);
        }

        if (resultKey === 'cost') {
          return polishNarrative([
            `${character.name} keeps the chase moving, but the success does not arrive cleanly.`,
            scene.costText,
            setup.partyRole === 'escaping'
              ? `${capitalizeSentence(opposition)} remains close enough to use that consequence in the next beat.`
              : `${capitalizeSentence(opposition)} gains a new detail, witness, or position to protect its escape.`,
            finalBeat ? `The objective can still be attempted: ${objective}.` : '',
            extra
          ]);
        }

        if (resultKey === 'setback') {
          return polishNarrative([
            scene.setbackText,
            setup.partyRole === 'escaping'
              ? `${capitalizeSentence(opposition)} closes the separation and enters the next scene from a stronger position.`
              : `${capitalizeSentence(opposition)} converts the delay into a cleaner escape line.`,
            finalBeat
              ? `The attempt to ${objective} is still playable, but it now begins from the worst position earned during the chase.`
              : 'The next challenge will be harder because the opposition can now act inside the same space.',
            extra
          ]);
        }

        return polishNarrative([
          'The DM ruling preserves the current balance but makes the exact choice part of the fiction.',
          scene.wildcard ? `${capitalizeSentence(stripEnding(scene.wildcard))}.` : '',
          finalBeat ? `Carry that ruling into the final attempt to ${objective}.` : 'The next beat begins from the situation just established.',
          extra
        ]);
      }


      function continueChase() {
        if (state.chase.advantage >= 3 || state.chase.advantage <= -3 || state.chase.peril >= 4 || state.chase.beat >= state.chase.totalBeats || timerExpired()) {
          finishChase();
          return;
        }

        state.chase.beat += 1;
        state.chase.viewBeat = state.chase.beat;
        state.chase.currentScene = buildScene();
        renderLiveScreen();
        root.querySelector('[data-scene-card]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      function showPreviousBeat() {
        if (state.chase.viewBeat <= 1) {
          openSetupForEdit();
          return;
        }
        state.chase.viewBeat -= 1;
        renderLiveScreen();
        root.querySelector('[data-scene-card]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      function showNextBeat() {
        if (state.chase.viewBeat < state.chase.beat) {
          state.chase.viewBeat += 1;
          renderLiveScreen();
          root.querySelector('[data-scene-card]').scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (!getHistoryEntry(state.chase.beat)) return;
        continueChase();
      }

      function openSetupForEdit() {
        if (!state.setup || state.chase.complete) return;
        timerWasRunningBeforeEdit = state.chase.timer.enabled && !state.chase.timer.paused;
        if (timerWasRunningBeforeEdit) toggleTimerPause();
        editingActiveChase = true;
        populateForm(state.setup);
        startButton.textContent = 'Apply changes and return';
        showScreen('setup');
        showStatus('Editing the active chase. Existing resolved beats and current progress will be preserved.', 'setup');
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      function applySetupEdits(setup) {
        const previousSetup = state.setup;
        const noResolvedBeats = state.chase.history.length === 0 && state.chase.beat === 1;
        state.setup = setup;
        state.chase.totalBeats = Math.max(setup.length, state.chase.beat);

        if (noResolvedBeats) state.chase.advantage = setup.startingAdvantage;

        const timerChanged = Boolean(previousSetup?.useTimer) !== Boolean(setup.useTimer)
          || Number(previousSetup?.timerMinutes || 0) !== Number(setup.timerMinutes || 0);
        if (timerChanged) {
          if (setup.useTimer) {
            state.chase.timer.enabled = true;
            state.chase.timer.durationSeconds = setup.timerMinutes * 60;
            state.chase.timer.remainingSeconds = setup.timerMinutes * 60;
            state.chase.timer.paused = !timerWasRunningBeforeEdit;
            state.chase.timer.endsAt = timerWasRunningBeforeEdit ? Date.now() + state.chase.timer.remainingSeconds * 1000 : null;
          } else {
            state.chase.timer = {
              enabled: false,
              durationSeconds: 0,
              endsAt: null,
              remainingSeconds: 0,
              paused: false
            };
          }
        } else if (state.chase.timer.enabled && timerWasRunningBeforeEdit) {
          state.chase.timer.paused = false;
          state.chase.timer.endsAt = Date.now() + state.chase.timer.remainingSeconds * 1000;
        }

        if (!getHistoryEntry(state.chase.beat)) state.chase.currentScene = buildScene();
        editingActiveChase = false;
        timerWasRunningBeforeEdit = false;
        startButton.textContent = 'Start chase';
        renderLiveScreen();
        showScreen('live');
        startTimerLoop();
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }


      function finishChase() {
        state.chase.active = false;
        state.chase.complete = true;
        stopTimerLoop();

        const outcome = determineFinalOutcome();
        root.querySelector('[data-finale-title]').textContent = outcome.title;
        root.querySelector('[data-finale-text]').textContent = outcome.text;
        root.querySelector('[data-finale-summary]').innerHTML = `
          <div><span>Resolved beats</span><strong>${state.chase.history.length}</strong></div>
          <div><span>Final advantage</span><strong>${escapeHtml(signedNumber(state.chase.advantage))}</strong></div>
          <div><span>Final peril</span><strong>${state.chase.peril} / 4</strong></div>
        `;
        showScreen('finale');
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      function determineFinalOutcome() {
        const role = state.setup.partyRole;
        const objective = stripEnding(objectiveText());
        const opposition = state.setup.oppositionName || 'the opposition';

        if (state.chase.failureReason === 'timer' || timerExpired()) {
          return role === 'escaping'
            ? { title: 'Time runs out', text: `The countdown reaches zero before you secure the objective: ${objective}. ${capitalizeSentence(opposition)} closes the remaining distance or cuts off the final route. The escape has failed, but the result becomes the next scene: capture, separation, a forced bargain, a lost objective, or a desperate confrontation.` }
            : { title: 'Time runs out', text: `The countdown reaches zero before you secure the objective: ${objective}. ${capitalizeSentence(opposition)} escapes the immediate chase or completes the action you were trying to prevent. Leave a clue, consequence, revealed destination, or damaged alliance that makes the failure playable.` };
        }

        if (state.chase.advantage >= 3) {
          return role === 'escaping'
            ? { title: 'The party breaks away', text: `You create a decisive separation from ${opposition} and secure the result you were running for: ${objective}. Preserve the method, sacrifice, or clever action that made the difference as part of the campaign fiction.` }
            : { title: 'The party closes the distance', text: `You catch, corner, or force a final decision from ${opposition}. The chase has delivered you to the objective: ${objective}. Resolve the immediate confrontation from this new position.` };
        }

        if (state.chase.advantage <= -3 || state.chase.peril >= 4) {
          return role === 'escaping'
            ? { title: 'The pursuit catches up', text: `${capitalizeSentence(opposition)} reaches you before the objective is cleanly secured: ${objective}. This is not a dead end. Begin the next scene with capture, separation, a forced bargain, a lost object, or a dangerous last stand shaped by the chase history.` }
            : { title: 'The target gets away', text: `${capitalizeSentence(opposition)} escapes immediate capture and prevents a clean result: ${objective}. Leave behind a clue, cost, witness, hostage, damaged alliance, or revealed destination that makes the loss playable.` };
        }

        if (state.chase.advantage > 0) {
          return { title: 'The party gains the upper hand', text: `You achieve or substantially secure the objective: ${objective}. The final peril still leaves a witness, resource loss, complication, or unfinished threat that belongs in the next scene.` };
        }

        if (state.chase.advantage < 0) {
          return { title: 'The opposition gains the upper hand', text: `${capitalizeSentence(opposition)} denies you the clean result you wanted: ${objective}. Convert that loss into a new playable situation rather than a punishment or narrative stop.` };
        }

        return { title: 'The chase ends in a dangerous balance', text: `Neither side secures a clean victory over the objective: ${objective}. End with a confrontation, bargain, divided route, mutual loss, or immediate decision that grows directly from the resolved beats.` };
      }

      function showScreen(name) {
        screens.forEach((screen) => {
          screen.hidden = screen.dataset.screen !== name;
        });
        if (toolGuide) toolGuide.hidden = name !== 'setup' || Boolean(state.chase.active || state.chase.complete);
      }

      function showStatus(message, area = 'setup') {
        const element = area === 'finale' ? root.querySelector('[data-finale-status]') : setupStatus;
        element.textContent = message;
        element.hidden = false;
        window.clearTimeout(Number(element.dataset.hideTimer || 0));
        const timer = window.setTimeout(() => {
          element.hidden = true;
        }, 5000);
        element.dataset.hideTimer = String(timer);
      }

      function exportJson(payload, filename) {
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        downloadBlob(blob, filename);
      }

      function exportSetup() {
        const setup = serializeSetup();
        exportJson({ type: 'mcp-chase-setup', version: 6, setup }, fileName(setup.sessionTitle || 'chase-setup', 'json'));
      }

      function exportProgress() {
        exportJson({ type: 'mcp-chase-session', version: 6, ...state }, fileName(state.setup?.sessionTitle || 'chase-session', 'json'));
      }

      async function importSetupFile(file) {
        const parsed = await readJsonFile(file);
        const setup = parsed?.setup;
        if (!setup || !Array.isArray(setup.characters)) throw new Error('This file does not contain a valid chase setup.');
        setup.chaseMode = setup.chaseMode || 'foot';
        populateForm(setup);
        showStatus('Saved setup loaded successfully.', 'setup');
      }

      async function importProgressFile(file) {
        const parsed = await readJsonFile(file);
        if (!parsed?.setup || !parsed?.chase) throw new Error('This file does not contain a valid saved chase session.');
        state = parsed;
        state.version = 6;
        state.setup.chaseMode = state.setup.chaseMode || 'foot';
        state.chase.viewBeat = clampNumber(state.chase.viewBeat || state.chase.beat, 1, state.chase.beat, state.chase.beat);
        state.chase.failureReason = state.chase.failureReason || null;
        state.chase.rejectedSceneKeys = Array.isArray(state.chase.rejectedSceneKeys) ? state.chase.rejectedSceneKeys : [];
        state.chase.timer = {
          enabled: false,
          durationSeconds: 0,
          endsAt: null,
          remainingSeconds: 0,
          paused: false,
          ...(state.chase.timer || {})
        };
        if (state.chase.timer.enabled && !state.chase.timer.paused && state.chase.timer.endsAt) updateTimerState();
        if (state.chase.complete) {
          finishChase();
        } else if (timerExpired()) {
          state.chase.failureReason = 'timer';
          finishChase();
        } else {
          renderLiveScreen();
          showScreen('live');
          startTimerLoop();
        }
      }


      function requestReset(mode = 'reset') {
        pendingResetMode = mode;
        modalReturnFocus = document.activeElement;
        const isNew = mode === 'new';
        root.querySelector('[data-confirm-title]').textContent = isNew ? 'Build another chase?' : 'Reset this chase?';
        root.querySelector('[data-confirm-message]').textContent = isNew
          ? 'This clears the completed chase and returns to an empty setup. Save the session or report first if you may need it later.'
          : 'This ends the current chase and returns to its setup. Resolved beats and unsaved progress will be cleared.';
        root.querySelector('[data-confirm-accept]').textContent = isNew ? 'Build new chase' : 'Reset chase';
        confirmModal.hidden = false;
        document.documentElement.classList.add('mcp-modal-open');
        window.setTimeout(() => root.querySelector('[data-confirm-cancel]').focus(), 0);
      }

      function closeConfirmModal() {
        confirmModal.hidden = true;
        document.documentElement.classList.remove('mcp-modal-open');
        pendingResetMode = null;
        if (modalReturnFocus && typeof modalReturnFocus.focus === 'function') modalReturnFocus.focus();
        modalReturnFocus = null;
      }

      function confirmReset() {
        const mode = pendingResetMode || 'reset';
        confirmModal.hidden = true;
        document.documentElement.classList.remove('mcp-modal-open');
        performReset(mode);
        pendingResetMode = null;
        modalReturnFocus = null;
      }

      function performReset(mode) {
        stopTimerLoop();
        const previousSetup = state.setup ? deepClone(state.setup) : null;
        state = createDefaultState();
        editingActiveChase = false;
        timerWasRunningBeforeEdit = false;
        startButton.textContent = 'Start chase';

        if (mode === 'reset' && previousSetup) {
          populateForm(previousSetup);
        } else {
          form.reset();
          characterList.innerHTML = '';
          addCharacter({ name: '', level: 1, roleOne: 'agile', roleTwo: 'observant', weakness: '' });
          refreshSpotlightOptions();
          updateConditionalFields();
          updateOppositionLanguage();
        }

        showScreen('setup');
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }


      function buildReport() {
        const setup = state.setup;
        const outcome = determineFinalOutcome();
        const environment = setup.environment === 'custom' ? setup.customEnvironment : ENVIRONMENT_LABELS[setup.environment];
        const lines = [
          setup.sessionTitle || 'Untitled Chase',
          '='.repeat((setup.sessionTitle || 'Untitled Chase').length),
          '',
          'SETUP',
          `Party role: ${setup.partyRole === 'escaping' ? 'Escaping' : 'Pursuing'}`,
          `Characters: ${setup.characters.map((character) => { const weakness = CHARACTER_WEAKNESSES.find(([value]) => value === character.weakness)?.[1] || 'None selected'; return `${character.name} (level ${character.level}, ${character.roleOne}, ${character.roleTwo}, weakness: ${weakness})`; }).join('; ')}`,
          `Opposition: ${setup.oppositionName}`,
          `Chase mode: ${CHASE_MODE_LABELS[setup.chaseMode || 'foot'] || setup.chaseMode}`,
          `Environment: ${environment}`,
          `Traits: ${setup.environmentTraits.length ? setup.environmentTraits.join(', ') : 'None selected'}`,
          `Difficulty: ${setup.difficulty}`,
          `Consequences: ${setup.consequences}`,
          `Tone: ${setup.tone}`,
          `Objective: ${setup.objectiveDetail || OBJECTIVE_LABELS[setup.objective]}`,
          '',
          'CHASE LOG'
        ];

        state.chase.history.forEach((entry) => {
          lines.push('');
          lines.push(`Beat ${entry.beat}: ${entry.sceneType}`);
          lines.push(entry.prompt);
          lines.push(`Check: ${entry.check}`);
          if (entry.rollTotal !== null) lines.push(`Roll: ${entry.rollTotal} vs DC ${entry.dc}`);
          lines.push(`Outcome: ${entry.outcomeTitle}`);
          lines.push(entry.outcomeText);
          lines.push(`Advantage change: ${signedNumber(entry.advantageChange)}; Peril change: ${signedNumber(entry.perilChange)}`);
        });

        lines.push('');
        lines.push('FINAL STATE');
        lines.push(outcome.title);
        lines.push(outcome.text);
        lines.push(`Final advantage: ${signedNumber(state.chase.advantage)}`);
        lines.push(`Final peril: ${state.chase.peril} / 4`);
        lines.push('');
        lines.push('Generated with the free Chase Builder by My Cat Portal Studio.');
        return lines.join('\n');
      }

      function exportReport() {
        const blob = new Blob([buildReport()], { type: 'text/plain;charset=utf-8' });
        downloadBlob(blob, fileName(state.setup?.sessionTitle || 'chase-report', 'txt'));
      }

      async function copyReport() {
        try {
          await navigator.clipboard.writeText(buildReport());
          showStatus('Report copied to the clipboard.', 'finale');
        } catch (error) {
          showStatus('Clipboard access was blocked. Use Export TXT report instead.', 'finale');
        }
      }

      function startTimerLoop() {
        stopTimerLoop();
        if (!state.chase.timer.enabled || state.chase.complete || state.chase.timer.paused) {
          renderTimer();
          return;
        }
        if (!state.chase.timer.endsAt) {
          state.chase.timer.endsAt = Date.now() + state.chase.timer.remainingSeconds * 1000;
        }
        timerInterval = window.setInterval(() => {
          updateTimerState();
          renderTimer();
        }, 250);
        updateTimerState();
        renderTimer();
      }

      function stopTimerLoop() {
        if (timerInterval) window.clearInterval(timerInterval);
        timerInterval = null;
      }

      function updateTimerState() {
        if (!state.chase.timer.enabled || state.chase.timer.paused || !state.chase.timer.endsAt || state.chase.complete) return;
        state.chase.timer.remainingSeconds = Math.max(0, Math.ceil((state.chase.timer.endsAt - Date.now()) / 1000));
        if (state.chase.timer.remainingSeconds <= 0) {
          state.chase.timer.remainingSeconds = 0;
          state.chase.failureReason = 'timer';
          stopTimerLoop();
          finishChase();
        }
      }

      function renderTimer() {
        const panel = root.querySelector('[data-timer-panel]');
        if (!state.chase.timer.enabled) {
          panel.hidden = true;
          return;
        }
        panel.hidden = false;
        root.querySelector('[data-timer-status]').textContent = formatTime(state.chase.timer.remainingSeconds);
        const pauseButton = root.querySelector('[data-timer-pause]');
        pauseButton.textContent = state.chase.timer.paused ? 'Resume' : 'Pause';
        panel.classList.toggle('is-paused', state.chase.timer.paused);
        panel.classList.toggle('is-critical', state.chase.timer.remainingSeconds <= 60);
      }

      function toggleTimerPause() {
        if (!state.chase.timer.enabled || state.chase.complete) return;
        if (state.chase.timer.paused) {
          state.chase.timer.paused = false;
          state.chase.timer.endsAt = Date.now() + state.chase.timer.remainingSeconds * 1000;
          startTimerLoop();
        } else {
          updateTimerState();
          state.chase.timer.paused = true;
          state.chase.timer.endsAt = null;
          stopTimerLoop();
          renderTimer();
        }
      }

      function resetTimer() {
        if (!state.chase.timer.enabled || state.chase.complete) return;
        state.chase.timer.remainingSeconds = state.chase.timer.durationSeconds;
        state.chase.failureReason = null;
        state.chase.timer.endsAt = state.chase.timer.paused ? null : Date.now() + state.chase.timer.durationSeconds * 1000;
        if (!state.chase.timer.paused) startTimerLoop();
        renderTimer();
      }

      function timerExpired() {
        return state.chase.timer.enabled && state.chase.timer.remainingSeconds <= 0;
      }

      function formatTime(seconds) {
        const safeSeconds = Math.max(0, Number(seconds) || 0);
        const minutes = Math.floor(safeSeconds / 60);
        const remainder = safeSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
      }

      function downloadBlob(blob, filename) {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      }

      function fileName(title, extension) {
        const safe = String(title || 'chase')
          .toLowerCase()
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 70) || 'chase';
        return `${safe}.${extension}`;
      }

      function readJsonFile(file) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              resolve(JSON.parse(String(reader.result || '')));
            } catch (error) {
              reject(new Error('The selected file is not valid JSON.'));
            }
          };
          reader.onerror = () => reject(new Error('The selected file could not be read.'));
          reader.readAsText(file);
        });
      }


      function deepClone(value) {
        return JSON.parse(JSON.stringify(value));
      }

      function clampNumber(value, min, max, fallback) {
        const number = Number(value);
        if (!Number.isFinite(number)) return fallback;
        return Math.min(max, Math.max(min, number));
      }

      function signedNumber(value) {
        const number = Number(value) || 0;
        return number > 0 ? `+${number}` : String(number);
      }

      function capitalizeWords(value) {
        return String(value || '').replace(/(^|[-\s])\S/g, (match) => match.toUpperCase()).replaceAll('-', ' ');
      }

      function escapeHtml(value) {
        return String(value ?? '')
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#039;');
      }

      function cssEscape(value) {
        if (window.CSS?.escape) return window.CSS.escape(String(value));
        return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
      }

      root.querySelector('[data-add-character]').addEventListener('click', () => addCharacter());

      characterList.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-remove-character]');
        if (!removeButton) return;
        const row = removeButton.closest('[data-character-row]');
        if (row) row.remove();
        if (!characterList.children.length) addCharacter();
        refreshSpotlightOptions();
      });

      characterList.addEventListener('input', () => refreshSpotlightOptions());
      form.elements.environment.addEventListener('change', updateConditionalFields);
      form.elements.useTimer.addEventListener('change', updateConditionalFields);
      form.querySelectorAll('input[name="partyRole"]').forEach((input) => input.addEventListener('change', updateOppositionLanguage));

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const setup = serializeSetup();
        if (!validateSetup(setup)) return;
        if (editingActiveChase && state.chase.active && !state.chase.complete) {
          applySetupEdits(setup);
        } else {
          startChase(setup);
        }
      });

      root.querySelector('[data-export-setup]').addEventListener('click', exportSetup);
      root.querySelector('[data-import-setup]').addEventListener('click', () => importInput.click());
      root.querySelector('[data-import-chase]').addEventListener('click', () => importChaseInput.click());

      importInput.addEventListener('change', async () => {
        const file = importInput.files?.[0];
        if (!file) return;
        try {
          await importSetupFile(file);
        } catch (error) {
          showStatus(error.message, 'setup');
        } finally {
          importInput.value = '';
        }
      });

      importChaseInput.addEventListener('change', async () => {
        const file = importChaseInput.files?.[0];
        if (!file) return;
        try {
          await importProgressFile(file);
        } catch (error) {
          showStatus(error.message, 'setup');
        } finally {
          importChaseInput.value = '';
        }
      });

      root.querySelector('[data-resolve-roll]').addEventListener('click', resolveRoll);
      root.querySelector('[data-roll-total]').addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          resolveRoll();
        }
      });

      root.querySelectorAll('[data-manual-result]').forEach((button) => {
        button.addEventListener('click', () => applyResolution(button.dataset.manualResult));
      });

      root.querySelector('[data-prev-scene]').addEventListener('click', showPreviousBeat);
      root.querySelector('[data-next-scene]').addEventListener('click', showNextBeat);
      root.querySelector('[data-resolution-back]').addEventListener('click', showPreviousBeat);
      root.querySelector('[data-resolution-next]').addEventListener('click', showNextBeat);
      root.querySelector('[data-timer-pause]').addEventListener('click', toggleTimerPause);
      root.querySelector('[data-timer-reset]').addEventListener('click', resetTimer);
      root.querySelector('[data-reroll-scene]').addEventListener('click', () => {
        if (state.chase.viewBeat !== state.chase.beat || getHistoryEntry(state.chase.beat)) return;
        if (state.chase.currentScene?.key) state.chase.rejectedSceneKeys.push(state.chase.currentScene.key);
        state.chase.currentScene = buildScene();
        renderLiveScreen();
      });

      root.querySelector('[data-back-to-setup]').addEventListener('click', openSetupForEdit);

      root.querySelector('[data-export-chase]').addEventListener('click', exportProgress);
      root.querySelector('[data-reset-chase]').addEventListener('click', () => requestReset('reset'));
      root.querySelector('[data-export-report]').addEventListener('click', exportReport);
      root.querySelector('[data-copy-report]').addEventListener('click', copyReport);
      root.querySelector('[data-new-chase]').addEventListener('click', () => requestReset('new'));
      root.querySelectorAll('[data-confirm-cancel]').forEach((button) => button.addEventListener('click', closeConfirmModal));
      root.querySelector('[data-confirm-accept]').addEventListener('click', confirmReset);
      confirmModal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeConfirmModal();
        }
        if (event.key === 'Tab') {
          const focusable = [...confirmModal.querySelectorAll('button:not([disabled])')];
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });

      addCharacter({ name: '', level: 1, roleOne: 'agile', roleTwo: 'observant', weakness: '' });
      updateConditionalFields();
      updateOppositionLanguage();


      app.setAttribute('data-ready', 'true');
    });
  })();